import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { customersAPI, documentsAPI, quoteAPI, xeroAPI, emailTemplatesAPI, variationsAPI, yeastarAPI } from '../lib/api';
import {
  formatDate,
  formatDateTime,
  formatSystemSize,
  formatCurrency,
  STATUS_DISPLAY_NAMES,
  STATUS_COLORS,
  PROJECT_STAGES,
} from '../lib/utils';
import api from '../lib/api';
import { runProposalJob } from '../lib/proposalJob';
import { PortalMessagesCard } from '../components/PortalMessagesCard';
import { PortalPreviewPanel } from '../components/PortalPreviewPanel';
import { AiImageNameDialog } from '../components/AiImageNameDialog';
import { DnoApplicationCard } from '../components/DnoApplicationCard';
import { CallRecordingPlayer } from '../components/CallRecordingPlayer';
import { ContractStatusCard, ContractStatusPill } from '../components/ContractStatusCard';
import { ScheduleInstallationDialog } from '../components/ScheduleInstallationDialog';
import { PostCreateInvoiceDialog } from '../components/PostCreateInvoiceDialog';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { DnoStatusEditable } from '../components/DnoStatusEditable';
import { StagePaymentsSection } from '../components/StagePaymentsSection';
import { JobsSwitcher } from '../components/JobsSwitcher';
import { EmailViewDialog } from '../components/EmailViewDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Home,
  Sun,
  Zap,
  FileText,
  Upload,
  Download,
  Clock,
  AlertCircle,
  CheckCircle,
  FileCheck,
  Loader2,
  RefreshCw,
  Sparkles,
  Image,
  File,
  Film,
  X,
  Receipt,
  Send,
  Eye,
  EyeOff,
  ExternalLink,
  Folder,
  FolderPlus,
  ChevronRight,
  MessageSquareText,
  ClipboardCheck,
  PoundSterling,
  CreditCard,
  Calendar,
  Users,
  Settings,
  Wrench,
  AlertTriangle,
  Archive,
  Link2,
  Unlink,
  UserPlus,
  Headphones,
  BellRing,
  Cloud,
  CloudUpload,
  FileSignature,
  FilePen,
  Bell,
  CheckCircle2,
  Star,
  ShieldCheck,
  PhoneCall,
  PhoneMissed,
  MessageCircle,
  Voicemail,
  Play,
  Pause,
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { InternalNotes } from '../components/InternalNotes';
import { InstallationChecklist } from '../components/InstallationChecklist';
import { RaiseTicketDialog } from '../components/RaiseTicketDialog';
import { SendEmailDialog } from '../components/SendEmailDialog';
import { WhatsAppComposeDialog } from '../components/WhatsAppComposeDialog';
import { ScheduledEmailsBanner } from '../components/ScheduledEmailsBanner';
import { SendSignLinkDialog } from '../components/SendSignLinkDialog';
import { ContractPackWizard } from '../components/ContractPackWizard';
import { ContractPacksSection } from '../components/ContractPacksSection';
import { XeroInvoiceDialog } from '../components/XeroInvoiceDialog';
import { XeroInvoicesCard } from '../components/XeroInvoicesCard';
import { UpdateProposalDialog } from '../components/UpdateProposalDialog';
import { VariationOfContractDialog } from '../components/VariationOfContractDialog';
import { RaiseVariationDialog } from '../components/RaiseVariationDialog';
import { LoaGenerateDialog } from '../components/LoaGenerateDialog';
import { SldGenerateDialog } from '../components/SldGenerateDialog';

export const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'details';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const fileInputRef = useRef(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, hasPermission } = useAuth();
  const canGenerateContracts = hasPermission('contracts', 'generate');
  const [uploading, setUploading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState(null);
  const aiResolverRef = useRef(null);
  const [generatingRams, setGeneratingRams] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, newStatus: '' });
  const [statusNotes, setStatusNotes] = useState('');
  // Post-status-change invoice prompt
  const [invoicePrompt, setInvoicePrompt] = useState({ open: false, type: 'deposit', label: null });
  const [updating, setUpdating] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [signLinkDialog, setSignLinkDialog] = useState({ open: false, doc: null });
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [backingUp, setBackingUp] = useState({});
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderMsg, setReminderMsg] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderSendEmail, setReminderSendEmail] = useState(false);
  const [reminderSendCalendar, setReminderSendCalendar] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [moveDocDialog, setMoveDocDialog] = useState({ open: false, doc: null });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailPresetTrigger, setEmailPresetTrigger] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [xeroInvoiceOpen, setXeroInvoiceOpen] = useState(false);
  const [portalPreviewOpen, setPortalPreviewOpen] = useState(false);
  const [contractPackOpen, setContractPackOpen] = useState(false);
  // Update Proposal flow — replace the active solar PV proposal and route
  // into Variation / Replace-pending / New based on the contract state.
  const [updateProposalOpen, setUpdateProposalOpen] = useState(false);
  const [variationDialogOpen, setVariationDialogOpen] = useState(false);
  const [raiseVariationOpen, setRaiseVariationOpen] = useState(false);
  const [loaGenerateOpen, setLoaGenerateOpen] = useState(false);

  // Ref to the Contract Packs section — used by the Contract Pack status
  // card's "Complete" pill to scroll the admin straight to the counter-sign
  // controls (rather than re-implementing the SignaturePad dialog here).
  const contractPacksSectionRef = useRef(null);
  const scrollToContractPacks = () => {
    // Make sure the Documents tab is mounted (smoothly switch first if
    // we're elsewhere), then scroll once the DOM has had a tick to update.
    if (activeTab !== 'documents') {
      setActiveTab('documents');
    }
    setTimeout(() => {
      if (contractPacksSectionRef.current) {
        contractPacksSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 120);
  };
  const [activityFilter, setActivityFilter] = useState('all');
  const [customerEmails, setCustomerEmails] = useState([]);
  
  // Portal account state
  const [portalAccount, setPortalAccount] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalLinkDialog, setPortalLinkDialog] = useState(false);
  const [raiseTicketOpen, setRaiseTicketOpen] = useState(false);
  const [waComposeOpen, setWaComposeOpen] = useState(false);
  const [unlinkedPortalUsers, setUnlinkedPortalUsers] = useState([]);
  const [selectedPortalUserId, setSelectedPortalUserId] = useState('');
  const [portalCreateMode, setPortalCreateMode] = useState(false);
  const [portalSaving, setPortalSaving] = useState(false);

  // Pop the email dialog with the install-scheduled template if we just
  // arrived from CustomerForm with `?prompt_email=installation_scheduled`.
  useEffect(() => {
    const promptEmail = searchParams.get('prompt_email');
    if (promptEmail && customer?.id) {
      setEmailPresetTrigger(promptEmail);
      setEmailDialogOpen(true);
      // strip the param so it doesn't re-fire on a refresh
      const url = new URL(window.location.href);
      url.searchParams.delete('prompt_email');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, searchParams.get('prompt_email')]);

  // Arrived from the customer edit form which detected a cost/equipment change
  // (`?action=variation` or `?action=contract`) — auto-open the right dialog.
  useEffect(() => {
    const action = searchParams.get('action');
    if (action && customer?.id) {
      if (action === 'variation') setRaiseVariationOpen(true);
      else if (action === 'contract') setContractPackOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('action');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, searchParams.get('action')]);

  // Auto-open the Generate LoA dialog when the DNO wizard deep-links here
  // via `?openLoa=1` (admin had no signed LoA on file when raising DNO).
  useEffect(() => {
    if (searchParams.get('openLoa') === '1' && customer?.id) {
      setLoaGenerateOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('openLoa');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, searchParams.get('openLoa')]);

  const API_URL = '';

  const fetchCustomer = async () => {
    try {
      const response = await customersAPI.getById(id);
      setCustomer(response.data);
      // Refresh variation/LoA signing-status chips
      fetchVariationSignStatus();
      // Extract unique folders from documents
      const uniqueFolders = [...new Set(
        (response.data.documents || [])
          .map(doc => doc.folder)
          .filter(f => f && f.trim())
      )];
      setFolders(uniqueFolders);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      toast.error('Customer not found');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const [refreshingCalendar, setRefreshingCalendar] = useState(false);
  const refreshCalendarInvite = async () => {
    setRefreshingCalendar(true);
    try {
      const r = await api.post(`/settings/calendar/refresh-future-invites?customer_id=${id}`);
      const d = r.data || {};
      if (d.updated > 0) toast.success('Calendar invite updated');
      else toast.info('No upcoming invite to update');
      await fetchCustomer();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reach the calendar — try again');
    } finally {
      setRefreshingCalendar(false);
    }
  };

  const fetchPortalAccount = async () => {
    setPortalLoading(true);
    try {
      const res = await api.get(`/customers/${id}/portal-account`);
      setPortalAccount(res.data);
    } catch {
      setPortalAccount(null);
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchPortalAccount();
  }, [id]);

  useEffect(() => {
    if (id) {
      api.get(`/email-timeline/customer/${id}?limit=200`).then(res => {
        setCustomerEmails(res.data.emails || []);
      }).catch(() => {});
    }
  }, [id]);

  const replaceVars = (text, c) => {
    if (!text || !c) return text || '';
    const portalUrl = 'https://app.helioses.co.uk/portal/login';
    const setupUrl = 'https://app.helioses.co.uk/portal/setup-password';
    const vars = {
      '{{first_name}}': c.first_name || '', '{{last_name}}': c.last_name || '',
      '{{name}}': `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      '{{email}}': c.email || '', '{{phone}}': c.phone || '',
      '{{address}}': c.address || '', '{{city}}': c.city || '', '{{postcode}}': c.postcode || '',
      '{{system_size}}': String(c.system_size || ''), '{{system_total_cost}}': String(c.system_total_cost || ''),
      '{{deposit_amount}}': String(c.deposit_amount || ''), '{{final_bill_amount}}': String(c.final_bill_amount || ''),
      '{{provisional_install_date}}': c.provisional_install_date || '', '{{confirmed_install_date}}': c.confirmed_install_date || '',
      '{{installation_type}}': c.installation_type || '', '{{installation_days}}': String(c.installation_days || 1), '{{panel_manufacturer}}': c.panel_manufacturer || '',
      '{{inverter_manufacturer}}': c.inverter_manufacturer || '', '{{battery_manufacturer}}': c.battery_manufacturer || '',
      '{{panel_quantity}}': String(c.panel_quantity || ''), '{{monthly_bill}}': String(c.monthly_bill || ''),
      '{{roof_type}}': c.roof_type || '', '{{status}}': STATUS_DISPLAY_NAMES[c.status] || c.status || '',
      '{{portal_url}}': portalUrl,
      '{{setup_url}}': setupUrl,
      '{{reset_link}}': setupUrl,
    };
    let result = text;
    for (const [k, v] of Object.entries(vars)) result = result.replaceAll(k, v);
    return result;
  };

  const [archiveWarning, setArchiveWarning] = useState({ open: false, newStatus: null });
  // Pop-out viewer for individual emails in the timeline
  const [emailViewer, setEmailViewer] = useState({ open: false, source: null, params: null });
  const openEmailViewer = (source, params) => setEmailViewer({ open: true, source, params });
  // Prompt that appears after marking a customer Completed asking whether to also archive
  const [archivePrompt, setArchivePrompt] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const handleArchiveNow = async () => {
    setArchiving(true);
    try {
      await customersAPI.updateStatus(id, {
        status: 'archived',
        notes: 'Archived after completion',
        send_email: false,
      });
      toast.success('Customer archived');
      setArchivePrompt(false);
      fetchCustomer();
    } catch {
      toast.error('Failed to archive customer');
    } finally {
      setArchiving(false);
    }
  };

  // Portal account handlers
  const openPortalLinkDialog = async () => {
    setPortalLinkDialog(true);
    setSelectedPortalUserId('');
    setPortalCreateMode(false);
    try {
      const res = await api.get('/admin/portal-users-unlinked');
      setUnlinkedPortalUsers(res.data || []);
    } catch {
      setUnlinkedPortalUsers([]);
    }
  };

  const handleLinkPortalAccount = async () => {
    if (!selectedPortalUserId) return;
    setPortalSaving(true);
    try {
      await api.post(`/customers/${id}/link-portal-account`, { portal_user_id: selectedPortalUserId });
      toast.success('Portal account linked');
      setPortalLinkDialog(false);
      fetchPortalAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link portal account');
    } finally {
      setPortalSaving(false);
    }
  };

  const handleUnlinkPortalAccount = async () => {
    setPortalSaving(true);
    try {
      await api.post(`/customers/${id}/link-portal-account`, { portal_user_id: '' });
      toast.success('Portal account unlinked');
      setPortalAccount(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink');
    } finally {
      setPortalSaving(false);
    }
  };

  const handleCreatePortalAccount = async () => {
    if (!customer) return;
    setPortalSaving(true);
    try {
      await api.post('/admin/portal-users', {
        email: customer.email,
        first_name: customer.first_name,
        last_name: customer.last_name,
        password: Math.random().toString(36).slice(-10),
      });
      // Now link it
      const res = await api.get(`/customers/${id}/portal-account`);
      if (res.data) {
        setPortalAccount(res.data);
        toast.success('Portal account created & linked. A welcome email with password setup link has been sent.');
      } else {
        // Auto-link may not have worked (different email), find & link manually
        const users = await api.get('/admin/portal-users-unlinked');
        const match = (users.data || []).find(u => u.email === customer.email);
        if (match) {
          await api.post(`/customers/${id}/link-portal-account`, { portal_user_id: match.id });
          fetchPortalAccount();
          toast.success('Portal account created & linked');
        }
      }
      setPortalLinkDialog(false);
    } catch (err) {
      if (err.response?.data?.detail?.includes('already registered')) {
        toast.error('A portal account with this email already exists. Use "Link Existing" instead.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to create portal account');
      }
    } finally {
      setPortalSaving(false);
    }
  };

  const openStatusDialog = async (newStatus) => {
    // Show archive warning for completed/archived
    if (newStatus === 'archived' || newStatus === 'completed') {
      setArchiveWarning({ open: true, newStatus });
      return;
    }
    doOpenStatusDialog(newStatus);
  };

  const handleConfirmArchive = () => {
    const { newStatus } = archiveWarning;
    setArchiveWarning({ open: false, newStatus: null });
    doOpenStatusDialog(newStatus);
  };

  const doOpenStatusDialog = async (newStatus) => {
    setStatusDialog({ open: true, newStatus });
    setStatusNotes('');
    setEmailEnabled(false);
    setSelectedTemplateId(null);
    // Fetch all email templates
    try {
      const res = await emailTemplatesAPI.getAll();
      const allTemplates = res.data || [];
      const relevant = allTemplates.filter(t => t.trigger === newStatus || t.is_manual);
      setAvailableTemplates(relevant);
      // Auto-load the trigger-specific template
      const tmpl = allTemplates.find(t => t.trigger === newStatus && t.active);
      if (tmpl) {
        setSelectedTemplateId(tmpl.id || null);
        setEmailSubject(replaceVars(tmpl.subject, customer));
        setEmailBody(replaceVars(tmpl.body_html, customer));
      } else {
        const name = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();
        setEmailSubject(`Helios Electrical and Solar - ${name}`);
        setEmailBody(`<p>Dear ${customer?.first_name || ''},</p><p>We are pleased to update you on the progress of your installation.</p><p>Your project has moved to: <b>${STATUS_DISPLAY_NAMES[newStatus] || newStatus}</b>.</p><p>If you have any questions, please don't hesitate to contact us.</p><p>Kind regards,<br>Helios Electrical and Solar</p>`);
      }
    } catch {
      setAvailableTemplates([]);
      setEmailSubject('');
      setEmailBody('');
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    if (templateId === 'default' || !templateId) {
      // Load default template for this trigger
      emailTemplatesAPI.getByTrigger(statusDialog.newStatus).then(res => {
        const tmpl = res.data || {};
        setEmailSubject(replaceVars(tmpl.subject || '', customer));
        setEmailBody(replaceVars(tmpl.body_html || '', customer));
      }).catch(() => {});
      return;
    }
    const tmpl = availableTemplates.find(t => t.id === templateId);
    if (tmpl) {
      setEmailSubject(replaceVars(tmpl.subject || '', customer));
      setEmailBody(replaceVars(tmpl.body_html || '', customer));
    }
  };

  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);

  const handleStatusChange = async (sendEmail = false) => {
    if (!statusDialog.newStatus) return;
    // If sending email, show confirmation first
    if (sendEmail && emailEnabled && customer?.email && emailBody) {
      setEmailConfirmOpen(true);
      return;
    }
    await executeStatusChange(false);
  };

  const executeStatusChange = async (sendEmail = false) => {
    setEmailConfirmOpen(false);
    if (!statusDialog.newStatus) return;
    setUpdating(true);
    try {
      await customersAPI.updateStatus(id, {
        status: statusDialog.newStatus,
        notes: statusNotes,
        send_email: false, // We handle email manually
      });

      // Send email if user chose to
      if (sendEmail && emailEnabled && customer?.email && emailBody) {
        setSendingEmail(true);
        try {
          await api.post(`/customers/${id}/send-status-email`, {
            subject: emailSubject,
            body_html: emailBody,
            cc_emails: ['hello@helioses.co.uk'],
          });
          toast.success(`Status updated & email sent to ${customer.email}`);
        } catch {
          toast.error('Status updated but email failed to send');
        } finally {
          setSendingEmail(false);
        }
      } else {
        toast.success('Status updated successfully');
      }

      setStatusDialog({ open: false, newStatus: '' });
      setStatusNotes('');

      // After status change, prompt for invoicing if relevant
      const newStatus = statusDialog.newStatus;
      if (newStatus === 'approved') {
        setInvoicePrompt({
          open: true,
          type: 'deposit',
          label: 'Quote approved — raise the deposit invoice?',
        });
      } else if (newStatus === 'in_progress_invoiced') {
        setInvoicePrompt({
          open: true,
          type: 'final',
          label: 'Status changed — raise the final payment invoice?',
        });
      } else if (newStatus === 'completed') {
        // Offer to archive the customer right after completion
        setArchivePrompt(true);
      }

      fetchCustomer();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const SUPPORTED_IMG = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

  // Ask AI for a name and let the user accept/override. Resolves to the chosen
  // display name (string) or null to keep the original filename.
  const askImageName = (file) => new Promise((resolve) => {
    aiResolverRef.current = resolve;
    const base = file.name.replace(/\.[^.]+$/, '');
    const previewUrl = URL.createObjectURL(file);
    setAiPrompt({ file, originalName: file.name, originalBase: base, previewUrl, suggestion: null, loading: true });
    documentsAPI.suggestImageName(file)
      .then((res) => setAiPrompt((p) => p ? { ...p, suggestion: res.data?.suggested_name || null, loading: false } : p))
      .catch(() => setAiPrompt((p) => p ? { ...p, suggestion: null, loading: false } : p));
  });

  const resolveAiPrompt = (chosenName) => {
    if (aiPrompt?.previewUrl) URL.revokeObjectURL(aiPrompt.previewUrl);
    const r = aiResolverRef.current;
    aiResolverRef.current = null;
    setAiPrompt(null);
    r?.(chosenName);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const failures = [];
    try {
      for (const file of files) {
        let displayName = null;
        if (SUPPORTED_IMG.includes(file.type)) {
          displayName = await askImageName(file);
        }
        try {
          await documentsAPI.upload(id, file, displayName);
        } catch (err) {
          const msg = err.response?.data?.detail || err.message || 'Upload failed';
          failures.push(`${file.name}: ${msg}`);
        }
      }
      if (failures.length === 0) {
        toast.success(files.length === 1 ? `Uploaded ${files[0].name}` : `Uploaded ${files.length} files`);
      } else if (failures.length < files.length) {
        toast.warning(`Uploaded ${files.length - failures.length} of ${files.length} — ${failures[0]}`);
      } else {
        toast.error(failures[0]);
      }
      fetchCustomer();
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const [remindingInvoice, setRemindingInvoice] = useState(null);

  const handleSendPaymentReminder = async (doc) => {
    setRemindingInvoice(doc.id);
    try {
      const res = await api.post(`/customers/${id}/invoices/${doc.source_invoice_id}/reminder`);
      toast.success(`Payment reminder emailed for ${res.data.invoice_number}`);
      fetchCustomer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reminder');
    } finally {
      setRemindingInvoice(null);
    }
  };

  const handleGenerateRams = async () => {
    setGeneratingRams(true);
    try {
      const res = await api.post(`/customers/${id}/rams/regenerate`);
      toast.success(`RAMS generated: ${res.data?.document?.filename || 'RAMS.pdf'}`);
      fetchCustomer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'RAMS generation failed');
    } finally {
      setGeneratingRams(false);
    }
  };

  const handleDownload = (doc) => {    const url = `${API_URL}/api/files/${id}/${doc.id}?download=true`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', doc.filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Document deletion is intentionally disabled in the CRM — files can only be
  // removed in SharePoint, which then syncs out of the CRM automatically.

  const [syncingFolder, setSyncingFolder] = useState(false);
  const [scheduledEmailRefresh, setScheduledEmailRefresh] = useState(0);
  const relativeTime = (iso) => {
    if (!iso) return '';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const mins = Math.floor(diff / 60000);
    return mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
  };
  const handleFolderSync = async () => {
    setSyncingFolder(true);
    try {
      const { data } = await api.post(`/customers/${id}/sharepoint-folder-sync`);
      toast.success(`SharePoint sync complete — ${data.imported || 0} imported, ${data.removed || 0} removed`);
      fetchCustomer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SharePoint sync failed');
    } finally {
      setSyncingFolder(false);
    }
  };

  // Tracks which solar_proposal doc is currently being re-extracted so the
  // button can show a spinner without locking up other docs in the list.
  const [reExtractingDocId, setReExtractingDocId] = useState(null);
  const [extractingDesignId, setExtractingDesignId] = useState(null);
  const [dnoReqLoading, setDnoReqLoading] = useState(null);
  const [dnoReqResult, setDnoReqResult] = useState(null);
  const [dnoReqCopied, setDnoReqCopied] = useState(false);

  const handleRequestDnoDetail = async (items) => {
    const key = items.join('+');
    setDnoReqLoading(key);
    try {
      const { data } = await api.post(`/customers/${id}/request-dno-detail`, { items, send_email: true });
      setDnoReqResult(data);
      setDnoReqCopied(false);
      toast.success(
        data.emailed
          ? `Request emailed to ${customer.email || 'the customer'}`
          : 'Secure request link created'
      );
      fetchCustomer();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not create the request');
    } finally {
      setDnoReqLoading(null);
    }
  };

  const handleExtractSystemDesign = async (docId) => {
    setExtractingDesignId(docId);
    try {
      await api.post(`/customers/${id}/system-design/extract?document_id=${encodeURIComponent(docId)}`);
      toast.success('System design diagram extracted and shared to the portal', { duration: 5000 });
      fetchCustomer();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'System design extraction failed');
    } finally {
      setExtractingDesignId(null);
    }
  };

  const handleReExtractProposal = async (docId) => {
    setReExtractingDocId(docId);
    try {
      const result = await runProposalJob(
        api,
        `/customers/${id}/documents/${docId}/re-extract/start`,
        null,
      );
      const ex = result?.extracted || {};
      const bits = [];
      if (ex.installed_capacity_kwp) bits.push(`${ex.installed_capacity_kwp} kWp`);
      if (ex.panel_count && ex.panel_wattage_w) bits.push(`${ex.panel_count}×${ex.panel_wattage_w}W`);
      if (ex.battery_usable_capacity_kwh) bits.push(`${ex.battery_usable_capacity_kwh} kWh battery`);
      toast.success(
        `Re-extracted: ${bits.join(' · ') || 'data refreshed'}${result?.mcs_document_refreshed ? ' · MCS doc refreshed' : ''}`,
        { duration: 6000 },
      );
      fetchCustomer();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Re-extract failed');
    } finally {
      setReExtractingDocId(null);
    }
  };

  const handleToggleVisibility = async (docId, currentVisibility) => {
    try {
      await documentsAPI.updateVisibility(id, docId, !currentVisibility);
      toast.success(currentVisibility ? 'Hidden from customer portal' : 'Visible in customer portal');
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handlePreviewDocument = (doc) => {
    setPreviewDoc(doc);
  };

  const handleTagDocument = async (docId, tag) => {
    try {
      await documentsAPI.updateTag(id, docId, tag);
      toast.success(tag ? `Document tagged as ${tag}` : 'Tag removed');
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to update tag');
    }
  };

  const [deleteImageTarget, setDeleteImageTarget] = useState(null);
  const [deletingImage, setDeletingImage] = useState(false);

  const handleDeleteImage = async () => {
    if (!deleteImageTarget) return;
    setDeletingImage(true);
    try {
      await documentsAPI.delete(id, deleteImageTarget.id);
      toast.success('Image deleted');
      setDeleteImageTarget(null);
      fetchCustomer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    } finally {
      setDeletingImage(false);
    }
  };

  const handleBackupToSharePoint = async (docId) => {
    setBackingUp(prev => ({ ...prev, [docId]: true }));
    try {
      const res = await api.post(`/customers/${id}/documents/${docId}/sharepoint-backup`);
      if (res.data?.already_synced) {
        toast.info('Already backed up to SharePoint');
      } else {
        toast.success('Document backed up to SharePoint');
      }
      fetchCustomer();
    } catch (err) {
      const msg = err.response?.data?.detail || 'SharePoint backup failed';
      toast.error(msg);
    } finally {
      setBackingUp(prev => {
        const next = { ...prev };
        delete next[docId];
        return next;
      });
    }
  };

  // Re-upload / repair a document whose file was lost (shows as corrupt).
  const repairInputRef = useRef(null);
  const [repairDocId, setRepairDocId] = useState(null);
  const [repairing, setRepairing] = useState(null);
  // Signing status (variation/LoA) for the "awaiting signature / signed" chips
  const [variationSignStatus, setVariationSignStatus] = useState({});

  const fetchVariationSignStatus = async () => {
    try {
      const { data } = await api.get(`/customers/${id}/variation-sign-status`);
      setVariationSignStatus(data || {});
    } catch (e) {
      // non-critical
    }
  };

  const triggerRepair = (docId) => {
    setRepairDocId(docId);
    if (repairInputRef.current) {
      repairInputRef.current.value = '';
      repairInputRef.current.click();
    }
  };

  const handleRepairFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const docId = repairDocId;
    if (!file || !docId) return;
    setRepairing(docId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/customers/${id}/documents/${docId}/replace`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File re-uploaded and backed up');
      fetchCustomer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-upload failed');
    } finally {
      setRepairing(null);
      setRepairDocId(null);
    }
  };


  const handleCreateReminder = async () => {
    if (!reminderMsg.trim() || !reminderDate) {
      toast.error('Message and date are required');
      return;
    }
    try {
      await api.post('/reminders', {
        customer_id: id,
        message: reminderMsg,
        due_at: new Date(reminderDate).toISOString(),
        send_email: reminderSendEmail,
        send_calendar_invite: reminderSendCalendar,
      });
      toast.success('Reminder set');
      setReminderDialogOpen(false);
      setReminderMsg('');
      setReminderDate('');
      setReminderSendEmail(false);
      setReminderSendCalendar(false);
    } catch {
      toast.error('Failed to set reminder');
    }
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      const trimmedName = newFolderName.trim();
      if (!folders.includes(trimmedName)) {
        setFolders([...folders, trimmedName]);
        toast.success(`Folder "${trimmedName}" created`);
      }
      setNewFolderName('');
      setShowNewFolderInput(false);
    }
  };

  const handleMoveDocument = async (docId, targetFolder) => {
    try {
      await documentsAPI.updateFolder(id, docId, targetFolder);
      toast.success(targetFolder ? `Moved to "${targetFolder}"` : 'Moved to root');
      setMoveDocDialog({ open: false, doc: null });
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to move document');
    }
  };

  const getDocumentsInFolder = (folderName) => {
    return (customer.documents || []).filter(doc => (doc.folder || '') === folderName);
  };

  const isPreviewable = (fileType) => {
    return fileType?.startsWith('image/') || fileType?.startsWith('video/') || fileType === 'application/pdf';
  };

  const handleDownloadQuote = async () => {
    try {
      const response = await quoteAPI.generatePDF(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${customer.first_name}_${customer.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Quote downloaded');
    } catch (error) {
      toast.error('Failed to generate quote');
    }
  };

  const handleSendSignLink = (docId) => {
    const doc = (customer?.documents || []).find((d) => d.id === docId);
    if (!doc) { toast.error('Document not found'); return; }
    setSignLinkDialog({ open: true, doc });
  };

  const handleGdprExport = async () => {
    if (!window.confirm(`Generate a full Subject Access Request (SAR) export for ${customer.first_name} ${customer.last_name}?\n\nThis bundles every piece of data we hold about the customer — profile, messages, contract packs, audit log, and every document file — into a single ZIP.\n\nThis action is logged in the audit trail.`)) return;
    try {
      toast.info('Building SAR export — this may take a moment for large records…');
      const response = await api.get(`/gdpr/export/customer/${id}/zip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `helios-SAR-${customer.first_name}-${customer.last_name}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('SAR export downloaded');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate SAR export';
      toast.error(message);
    }
  };

  const handleSendToXero = async () => {
    try {
      await xeroAPI.sendInvoice(id);
      toast.success('Invoice sent to Xero');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send to Xero';
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  if (!customer) {
    return null;
  }

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return <Image className="w-5 h-5 text-blue-500" />;
    }
    if (fileType?.startsWith('video/')) {
      return <Film className="w-5 h-5 text-purple-500" />;
    }
    return <File className="w-5 h-5 text-slate-500" />;
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="customer-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/customers')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
                {customer.first_name} {customer.last_name}
              </h1>
              <div className="mt-2">
                <JobsSwitcher customerId={customer.id} />
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={`${STATUS_COLORS[customer.status]} border`}>
                  {STATUS_DISPLAY_NAMES[customer.status]}
                </Badge>
                <PaymentStatusBadge customer={customer} variant="full" />
                <DnoStatusEditable
                  customer={customer}
                  isAdmin={!!user?.is_admin}
                  onUpdated={(updated) => setCustomer(prev => ({ ...prev, dno_manual_status: updated.dno_manual_status, dno_manual_status_note: updated.dno_manual_status_note, dno_export_limited: updated.dno_export_limited, dno_export_limit_note: updated.dno_export_limit_note }))}
                />
                <ContractStatusPill
                  customerId={customer.id}
                  onClick={scrollToContractPacks}
                />
                {customer.contract_signed_date && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700"
                    title="Date the customer signed their contract pack"
                    data-testid="contract-signed-date-pill"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Contract signed {formatDate(customer.contract_signed_date)}
                  </span>
                )}
                {['scheduled', 'in_progress', 'in_progress_invoiced'].includes(customer.status) &&
                  !customer.documents?.some(d => d.doc_tag === 'rams') && (
                  <button
                    type="button"
                    onClick={handleGenerateRams}
                    disabled={generatingRams}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-60"
                    title="No RAMS document yet — click to generate one"
                    data-testid="no-rams-badge"
                  >
                    {generatingRams ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-3 h-3" />
                    )}
                    {generatingRams ? 'Generating RAMS…' : 'No RAMS yet'}
                  </button>
                )}
                {customer.status === 'completed' && (
                  <button
                    type="button"
                    onClick={() => setArchivePrompt(true)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition-colors"
                    title="Move this customer to Archive/Lost"
                    data-testid="archive-customer-pill"
                  >
                    <Archive className="w-3 h-3" />
                    Archive Customer
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {customer.phone && (
              <Button
                variant="outline"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={async () => {
                  try {
                    await yeastarAPI.dial(customer.phone);
                    toast.success('Ringing your desk phone — it will connect to the customer when you pick up');
                  } catch (e) {
                    const detail = e.response?.data?.detail || '';
                    if (e.response?.status === 400 && detail.includes('extension')) {
                      toast.error(detail);
                    } else {
                      toast.message('Opening your device dialer…');
                      window.location.href = `tel:${customer.phone}`;
                    }
                  }
                }}
                data-testid="call-customer-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Customer
              </Button>
            )}
            <Button variant="outline" onClick={() => setEmailDialogOpen(true)} data-testid="email-customer-btn">
              <Mail className="w-4 h-4 mr-2" />
              Email Customer
            </Button>
            {(customer.phone || customer.whatsapp_number) && (
              <Button
                variant="outline"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setWaComposeOpen(true)}
                data-testid="whatsapp-customer-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Message on WhatsApp
              </Button>
            )}
            <Button variant="outline" onClick={() => setRaiseTicketOpen(true)} data-testid="raise-ticket-btn">
              <Headphones className="w-4 h-4 mr-2" />
              Raise Ticket
            </Button>
            <Button variant="outline" onClick={() => setPortalPreviewOpen(true)} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="view-portal-btn">
              <Eye className="w-4 h-4 mr-2" />
              View Portal
            </Button>
            <Button variant="outline" onClick={() => setXeroInvoiceOpen(true)} className="border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="xero-invoice-btn">
              <FileText className="w-4 h-4 mr-2" />
              Send to Xero
            </Button>
            <Button variant="outline" onClick={handleDownloadQuote} data-testid="download-quote-btn">
              <FileText className="w-4 h-4 mr-2" />
              Download Quote
            </Button>
            {canGenerateContracts && (
            <Button
              variant="outline"
              onClick={() => setContractPackOpen(true)}
              className="border-violet-200 text-violet-700 hover:bg-violet-50"
              data-testid="contract-pack-btn"
            >
              <FileSignature className="w-4 h-4 mr-2" />
              Contract Pack
            </Button>
            )}
            {canGenerateContracts && (
            <Button
              variant="outline"
              onClick={() => setUpdateProposalOpen(true)}
              className="border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="update-proposal-btn"
              title="Upload a revised solar PV proposal — auto-applies the new figures, then routes to Variation / Replace / New based on the contract state."
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Update Proposal
            </Button>
            )}
            {canGenerateContracts && (
            <Button
              variant="outline"
              onClick={() => setRaiseVariationOpen(true)}
              className="border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="raise-variation-btn"
              title="Manually raise a Variation of Contract. Smart-routes based on the current contract state: opens the wizard if not yet sent, allows 'Mark signed in DocHub' if sent-but-unsigned, or opens the variation dialog if signed."
            >
              <FileSignature className="w-4 h-4 mr-2" />
              Raise Variation
            </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setLoaGenerateOpen(true)}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="loa-generate-btn"
              title="Generate a customer-personalised Letter of Authority for the DNO application. Same Helios branding as the contract; sent for signing via the existing public sign flow."
            >
              <FilePen className="w-4 h-4 mr-2" />
              Letter of Authority
            </Button>
            <SldGenerateDialog
              customer={customer}
              onGenerated={() => fetchCustomer()}
              triggerLabel="Create SLD"
              triggerClassName="inline-flex items-center justify-center gap-2 h-10 px-4 py-2 text-sm font-medium rounded-md border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
            />
            <Button variant="outline" onClick={() => setReminderDialogOpen(true)} className="border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="set-reminder-btn">
              <BellRing className="w-4 h-4 mr-2" />
              Reminder
            </Button>
            <Button
              variant="outline"
              onClick={handleGdprExport}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="gdpr-export-btn"
              title="GDPR Subject Access Request (SAR) export — bundles every piece of data we hold about this customer into a single ZIP. Action is audit-logged."
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              GDPR Export
            </Button>
            <Link to={`/customers/${id}/edit`}>
              <Button variant="outline" data-testid="edit-customer-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </Link>
            <Select
              value={customer.status}
              onValueChange={(value) => openStatusDialog(value)}
            >
              <SelectTrigger className="w-[200px]" data-testid="status-select">
                <SelectValue placeholder="Change Status" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_STAGES.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {STATUS_DISPLAY_NAMES[stage]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Scheduled emails for this customer */}
        <div className="mb-4">
          <ScheduledEmailsBanner customerId={id} refreshKey={scheduledEmailRefresh} />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">
              Documents ({customer.documents?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">
              <MessageSquareText className="w-4 h-4 mr-1" />
              Internal Notes
            </TabsTrigger>
            <TabsTrigger value="checklist" data-testid="tab-checklist">
              <ClipboardCheck className="w-4 h-4 mr-1" />
              Checklist
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              Timeline
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Information */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-['Manrope']">
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <a href={`mailto:${customer.email}`} className="text-amber-600 hover:text-amber-700">
                        {customer.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <a href={`tel:${customer.phone}`} className="text-slate-900">
                        {customer.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Address</p>
                      <p className="text-slate-900">{customer.address}</p>
                      {(customer.city || customer.state || customer.postcode) && (
                        <p className="text-slate-900">
                          {[customer.city, customer.state, customer.postcode].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {customer.postcode && (
                        <span
                          data-testid="coverage-badge"
                          className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            customer.in_coverage_area
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${customer.in_coverage_area ? 'bg-green-500' : 'bg-red-500'}`} />
                          {customer.in_coverage_area ? 'In Coverage Area' : 'Out of Coverage Area'}
                        </span>
                      )}
                      <div className="mt-1">
                        {customer.mpan ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-emerald-100 text-emerald-700" data-testid="detail-mpan-badge">
                            MPAN: {customer.mpan}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate(`/customers/${customer.id}/edit`)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                            data-testid="detail-mpan-missing"
                            title="ENA didn't return an MPAN for this address — click to add it manually"
                          >
                            <AlertCircle className="w-3 h-3" /> MPAN missing — add it
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* DNO readiness — only for customers where the MPAN/cut-out
                      requirement applies (new customers from Sep 2026 on). */}
                  {customer.dno_requirements_enforced && (() => {
                    const hasMpan = !!(customer.mpan && String(customer.mpan).trim());
                    const hasCutout = !!(customer.cutout_image_url && String(customer.cutout_image_url).trim())
                      || (customer.documents || []).some((d) => d.doc_tag === 'cutout_image');
                    const ready = hasMpan && hasCutout;
                    return (
                      <div className="flex items-start gap-3" data-testid="dno-readiness">
                        <FileCheck className={`w-5 h-5 mt-0.5 ${ready ? 'text-emerald-500' : 'text-amber-500'}`} />
                        <div>
                          <p className="text-sm text-slate-500">DNO application readiness</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${hasMpan ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`} data-testid="dno-ready-mpan">
                              {hasMpan ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />} MPAN {hasMpan ? 'on file' : 'missing'}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${hasCutout ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`} data-testid="dno-ready-cutout">
                              {hasCutout ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />} Cut-out {hasCutout ? 'on file' : 'missing'}
                            </span>
                            {ready && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-600 text-white" data-testid="dno-ready-badge">
                                <CheckCircle className="w-3 h-3" /> Ready to raise DNO
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Request MPAN / cut-out from the customer (email + secure
                      link; customer can also upload from their portal). */}
                  <div className="flex items-start gap-3" data-testid="dno-request-actions">
                    <Send className="w-5 h-5 mt-0.5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Request DNO details from customer</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        <Button
                          variant="outline" size="sm" className="h-8 text-xs"
                          onClick={() => handleRequestDnoDetail(['mpan'])}
                          disabled={dnoReqLoading === 'mpan'}
                          data-testid="request-mpan-btn"
                        >
                          {dnoReqLoading === 'mpan' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                          Request MPAN
                        </Button>
                        <Button
                          variant="outline" size="sm" className="h-8 text-xs"
                          onClick={() => handleRequestDnoDetail(['cutout'])}
                          disabled={dnoReqLoading === 'cutout'}
                          data-testid="request-cutout-btn"
                        >
                          {dnoReqLoading === 'cutout' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                          Request cut-out photo
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="h-8 text-xs text-slate-500"
                          onClick={() => handleRequestDnoDetail(['mpan', 'cutout'])}
                          disabled={dnoReqLoading === 'mpan+cutout'}
                          data-testid="request-both-btn"
                        >
                          {dnoReqLoading === 'mpan+cutout' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                          Request both
                        </Button>
                      </div>
                    </div>
                  </div>
                    <>
                      {customer.company_name && (
                        <div className="flex items-center gap-3">
                          <Settings className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-sm text-slate-500">Company</p>
                            <p className="text-slate-900">{customer.company_name}</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {customer.assigned_users?.length > 0 && (
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-slate-500">Assigned Staff</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {customer.assigned_users.map((u) => (
                            <Badge key={u.id} className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                              {u.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {(customer.referral_source || customer.referred_by_name) && (
                    <div className="flex items-start gap-3">
                      <UserPlus className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-slate-500">Referral</p>
                        {customer.referral_source && (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs mr-1" data-testid="referral-source-badge">
                            {({
                              word_of_mouth: 'Word of Mouth', google: 'Google', facebook: 'Facebook/IG',
                              website: 'Website', flyer: 'Flyer', referral: 'Customer Referral',
                              trade_show: 'Trade Show', yell: 'Yell/Directory', other: 'Other',
                            })[customer.referral_source] || customer.referral_source}
                          </Badge>
                        )}
                        {customer.referred_by_name && (
                          <p className="text-sm text-slate-700 mt-1">Referred by: <strong>{customer.referred_by_name}</strong></p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Portal Account */}
              <Card className="border-slate-200" data-testid="portal-account-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-blue-500" />
                    Customer Portal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {portalLoading ? (
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : portalAccount ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm text-emerald-800 truncate">
                            {portalAccount.first_name} {portalAccount.last_name}
                          </p>
                          <p className="text-xs text-emerald-600 truncate">{portalAccount.email}</p>
                        </div>
                        <Badge className={portalAccount.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                          {portalAccount.active !== false ? 'Active' : 'Disabled'}
                        </Badge>
                      </div>
                      {portalAccount.shared_via_group ? (
                        <div className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg p-2.5" data-testid="portal-shared-note">
                          <p className="text-blue-700 font-medium mb-1">Shared login across this customer's projects</p>
                          This project is covered by the customer's existing portal account — they can see it by switching projects in their portal.
                          {portalAccount.linked_customer_id && (
                            <button
                              type="button"
                              className="block mt-1 text-blue-600 underline hover:text-blue-800"
                              onClick={() => navigate(`/customers/${portalAccount.linked_customer_id}`)}
                              data-testid="portal-open-owning-project"
                            >
                              Manage the account on the main project →
                            </button>
                          )}
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-amber-700 border-amber-300 hover:bg-amber-50"
                          onClick={handleUnlinkPortalAccount}
                          disabled={portalSaving}
                          data-testid="unlink-portal-btn"
                        >
                          {portalSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlink className="w-4 h-4 mr-2" />}
                          Unlink Portal Account
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-500">No portal account linked to this customer.</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={openPortalLinkDialog}
                        data-testid="link-portal-btn"
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        Link or Create Portal Account
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Portal Messages */}
              {portalAccount && (
                <PortalMessagesCard customerId={id} />
              )}

              {/* Last portal login summary */}
              {(() => {
                const portalLogins = (customer.activity_history || []).filter(a => a.type === 'portal_login');
                if (portalLogins.length === 0) return null;
                const latest = portalLogins.reduce((a, b) => (a.timestamp > b.timestamp ? a : b));
                return (
                  <Card className="border-cyan-200 bg-cyan-50/40" data-testid="last-portal-login">
                    <CardContent className="py-3 flex items-center gap-3 text-sm">
                      <Users className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                      <span className="text-slate-700">
                        Last portal login{' '}
                        <strong className="text-cyan-900">{formatDateTime(latest.timestamp)}</strong>
                        <span className="text-slate-500"> · {portalLogins.length} login{portalLogins.length === 1 ? '' : 's'} total</span>
                      </span>
                    </CardContent>
                  </Card>
                );
              })()}


              {/* Property Details */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-['Manrope']">
                    Property Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Home className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Property Type</p>
                      <p className="text-slate-900 capitalize">{customer.property_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Roof Type</p>
                      <p className="text-slate-900 capitalize">{customer.roof_type || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Sun className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Roof Orientation</p>
                      <p className="text-slate-900 capitalize">{customer.roof_orientation || 'Not specified'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* System Cost & Dates */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-['Manrope']">
                    System Cost & Dates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100" data-testid="system-cost-display">
                    <div className="flex items-center gap-2 mb-1">
                      <PoundSterling className="w-5 h-5 text-amber-600" />
                      <p className="text-sm text-amber-700 font-medium">System Price (ex VAT)</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">
                      {formatCurrency(customer.system_total_cost)}
                    </p>
                    {Number(customer.vat_rate) > 0 ? (
                      <p className="text-xs text-amber-700 mt-1" data-testid="detail-vat-line">
                        + VAT {customer.vat_rate}% ({formatCurrency((Number(customer.system_total_cost) || 0) * (Number(customer.vat_rate) / 100))})
                        {' '}= <strong>{formatCurrency((Number(customer.system_total_cost) || 0) * (1 + Number(customer.vat_rate) / 100))} inc VAT</strong>
                      </p>
                    ) : (
                      <p className="text-xs text-amber-700 mt-1" data-testid="detail-vat-line">Ex-VAT price — set VAT to 20% on the edit screen if a commercial customer.</p>
                    )}
                    {/* Provenance chip — clickable when the total was pulled
                        from a proposal upload via Gemini. Opens the source
                        PDF in a new tab so admins can trace the figure. */}
                    {customer.mcs_solar_pv_source_document_id && (() => {
                      const src = (customer.documents || []).find(d => d.id === customer.mcs_solar_pv_source_document_id);
                      const label = src?.filename || src?.name || 'Solar PV proposal';
                      return (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const resp = await documentsAPI.download(customer.id, customer.mcs_solar_pv_source_document_id);
                              const blob = new Blob([resp.data], { type: 'application/pdf' });
                              window.open(URL.createObjectURL(blob), '_blank');
                            } catch (e) { toast.error('Could not open source proposal'); }
                          }}
                          className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-amber-700 hover:text-amber-900 bg-white border border-amber-300 rounded-full pl-1.5 pr-2 py-0.5 transition-colors"
                          title={`Open the source proposal: ${label}`}
                          data-testid="system-cost-source-chip"
                        >
                          <Sparkles className="w-2.5 h-2.5" />
                          Extracted from: <span className="normal-case font-medium tracking-normal ml-0.5 max-w-[200px] truncate inline-block align-bottom">{label}</span>
                        </button>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100" data-testid="provisional-date-display">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <p className="text-sm text-blue-700 font-medium">Provisional Date</p>
                      </div>
                      <p className="text-base font-semibold text-slate-900">
                        {customer.provisional_install_date ? formatDate(customer.provisional_install_date) : 'Not set'}
                      </p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100" data-testid="confirmed-date-display">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <p className="text-sm text-emerald-700 font-medium">Confirmed Date</p>
                      </div>
                      <p className="text-base font-semibold text-slate-900">
                        {customer.confirmed_install_date ? formatDate(customer.confirmed_install_date) : 'Not set'}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100" data-testid="installation-days-display">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <p className="text-sm text-amber-700 font-medium">Installation Duration</p>
                    </div>
                    <p className="text-base font-semibold text-slate-900">
                      {customer.installation_days || 1} {(customer.installation_days || 1) === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                  <Button
                    onClick={() => setScheduleDialogOpen(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="schedule-installation-btn"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Installation (find available dates)
                  </Button>
                  {customer.calendar_synced_at && (() => {
                    const outOfDate = customer.calendar_dirty_at &&
                      new Date(customer.calendar_dirty_at) > new Date(customer.calendar_synced_at);
                    if (outOfDate) {
                      return (
                        <div className="flex items-center justify-center gap-1.5 text-xs text-amber-600" data-testid="calendar-synced-indicator">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span>Calendar out of date</span>
                          <button
                            type="button"
                            onClick={refreshCalendarInvite}
                            disabled={refreshingCalendar}
                            className="underline font-medium hover:text-amber-700 disabled:opacity-50"
                            data-testid="calendar-refresh-now-btn"
                          >
                            {refreshingCalendar ? 'Refreshing…' : 'Refresh now'}
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center justify-center gap-1.5 text-xs text-emerald-600" data-testid="calendar-synced-indicator">
                        <CheckCircle className="w-3.5 h-3.5" />
                        <span>Calendar synced · invite updated {new Date(customer.calendar_synced_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Energy & System */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-['Manrope']">
                    Energy & System
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Installation Type</p>
                      <p className="text-slate-900 capitalize">{customer.installation_type}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Monthly Bill</p>
                      <p className="text-xl font-semibold text-slate-900">
                        {formatCurrency(customer.monthly_bill)}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Monthly Usage</p>
                      <p className="text-xl font-semibold text-slate-900">
                        {customer.monthly_usage} kWh
                      </p>
                    </div>
                  </div>
                  {customer.annual_usage > 0 && (
                    <div className="p-4 bg-slate-50 rounded-lg" data-testid="annual-usage-detail">
                      <p className="text-sm text-slate-500">Estimated Annual Usage</p>
                      <p className="text-xl font-semibold text-slate-900">
                        {customer.annual_usage} kWh
                      </p>
                    </div>
                  )}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-sm text-amber-700">Proposed System Size</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {formatSystemSize(customer.system_size)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Equipment Details */}
              {(customer.panel_manufacturer || customer.inverter_manufacturer || customer.battery_manufacturer || customer.consumer_unit_manufacturer || (customer.additional_extras || '').trim() || (Array.isArray(customer.additional_equipment) && customer.additional_equipment.length > 0)) && (
                <Card className="border-slate-200" data-testid="equipment-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold font-['Manrope']">
                      Equipment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {customer.panel_manufacturer && (
                        <div className="p-3 bg-slate-50 rounded-lg" data-testid="panel-info">
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Panels</p>
                          <p className="font-medium text-slate-900">{customer.panel_manufacturer}</p>
                          {customer.panel_model && <p className="text-sm text-slate-600" data-testid="panel-model">Model: {customer.panel_model}</p>}
                          {customer.panel_quantity > 0 && <p className="text-sm text-slate-600" data-testid="panel-qty">Qty: {customer.panel_quantity}</p>}
                        </div>
                      )}
                      {customer.inverter_manufacturer && (
                        <div className="p-3 bg-slate-50 rounded-lg" data-testid="inverter-info">
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Inverter</p>
                          <p className="font-medium text-slate-900">{customer.inverter_manufacturer}</p>
                          {customer.inverter_model && <p className="text-sm text-slate-600" data-testid="inverter-model">Model: {customer.inverter_model}</p>}
                          {customer.inverter_quantity > 0 && <p className="text-sm text-slate-600" data-testid="inverter-qty">Qty: {customer.inverter_quantity}</p>}
                          {customer.gen_meter_model && <p className="text-sm text-slate-600" data-testid="gen-meter-model">Gen. meter: {customer.gen_meter_model}</p>}
                        </div>
                      )}
                      {customer.battery_manufacturer && (
                        <div className="p-3 bg-slate-50 rounded-lg" data-testid="battery-info">
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Battery</p>
                          <p className="font-medium text-slate-900">{customer.battery_manufacturer}</p>
                          {customer.battery_model && <p className="text-sm text-slate-600" data-testid="battery-model">Model: {customer.battery_model}</p>}
                          {customer.battery_quantity > 0 && <p className="text-sm text-slate-600" data-testid="battery-qty">Qty: {customer.battery_quantity}</p>}
                        </div>
                      )}
                      {customer.consumer_unit_manufacturer && (
                        <div className="p-3 bg-slate-50 rounded-lg" data-testid="consumer-unit-info">
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Consumer Unit</p>
                          <p className="font-medium text-slate-900">{customer.consumer_unit_manufacturer}</p>
                          {customer.consumer_unit_model && <p className="text-sm text-slate-600" data-testid="consumer-unit-model">Model: {customer.consumer_unit_model}</p>}
                        </div>
                      )}
                      {Array.isArray(customer.additional_equipment) && customer.additional_equipment
                        .filter((l) => l && (l.manufacturer || l.model))
                        .map((l, idx) => {
                          const catLabel = { panel: 'Panels', inverter: 'Inverter', battery: 'Battery', ev_charger: 'EV Charger' }[l.category] || 'Equipment';
                          return (
                            <div key={`addl-eq-${idx}`} className="p-3 bg-amber-50 rounded-lg border border-amber-100" data-testid={`additional-equipment-info-${idx}`}>
                              <p className="text-xs text-amber-600 uppercase tracking-wider">{catLabel} (additional)</p>
                              <p className="font-medium text-slate-900">{l.manufacturer}</p>
                              {l.model && <p className="text-sm text-slate-600">Model: {l.model}</p>}
                              {l.quantity > 0 && <p className="text-sm text-slate-600">Qty: {l.quantity}</p>}
                              {l.warranty_years && <p className="text-sm text-slate-600">Warranty: {l.warranty_years} yrs</p>}
                            </div>
                          );
                        })}
                    </div>
                    {(customer.additional_extras || '').trim() && (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100" data-testid="additional-extras-info">
                        <p className="text-xs text-amber-600 uppercase tracking-wider mb-1">Additional Extras</p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{customer.additional_extras}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* DNO Application Details */}
              <DnoApplicationCard
                customer={customer}
                isAdmin={!!user?.is_admin}
                onUpdated={fetchCustomer}
              />

              {/* Contract Pack status — Sent / Customer signed / Complete */}
              <ContractStatusCard
                customerId={customer.id}
                onOpenWizard={() => setContractPackOpen(true)}
                onScrollToPacks={scrollToContractPacks}
              />

              {/* Payments */}
              <Card className="border-slate-200" data-testid="payments-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                    <PoundSterling className="w-5 h-5" />
                    Payments
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {customer.on_finance && (parseFloat(customer.finance_amount) || 0) > 0 && (
                    <div className="p-4 rounded-lg border border-purple-200 bg-purple-50/60" data-testid="finance-summary">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm text-purple-700 font-medium mb-1 flex items-center gap-1.5">
                            <CreditCard className="w-4 h-4" /> On Finance
                          </p>
                          <p className="text-xl font-bold text-slate-900">{formatCurrency(customer.finance_amount)}</p>
                          <p className="text-xs text-purple-700 mt-1">
                            Out-of-pocket: {formatCurrency(Math.max(((parseFloat(customer.system_total_cost) || 0) * (1 + (Number(customer.vat_rate) || 0) / 100)) - (parseFloat(customer.finance_amount) || 0), 0))}
                            <span className="text-slate-500"> · split 25% / 75% below</span>
                          </p>
                        </div>
                        <div className="text-right text-xs text-slate-500">
                          <p>Total (inc VAT): <span className="font-semibold text-slate-700">{formatCurrency((parseFloat(customer.system_total_cost) || 0) * (1 + (Number(customer.vat_rate) || 0) / 100))}</span></p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg border border-indigo-100 bg-indigo-50/50">
                      <p className="text-sm text-indigo-700 font-medium mb-1">Deposit (25%)</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(customer.deposit_amount)}</p>
                      {customer.deposit_due_date && (
                        <p className="text-xs text-slate-500 mt-1">Due: {formatDate(customer.deposit_due_date)}</p>
                      )}
                      {customer.deposit_paid_date && (
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Paid {formatDate(customer.deposit_paid_date)}
                        </p>
                      )}
                    </div>
                    <div className="p-4 rounded-lg border border-orange-100 bg-orange-50/50">
                      <p className="text-sm text-orange-700 font-medium mb-1">Final Bill (75%)</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(customer.final_bill_amount)}</p>
                      {customer.final_bill_due_date && (
                        <p className="text-xs text-slate-500 mt-1">Due: {formatDate(customer.final_bill_due_date)}</p>
                      )}
                      {customer.final_bill_paid_date && (
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Paid {formatDate(customer.final_bill_paid_date)}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stage Payments — interim invoices between deposit and final */}
              <StagePaymentsSection
                customer={customer}
                isAdmin={!!user?.is_admin}
                onChanged={() => fetchCustomer && fetchCustomer()}
              />

              {/* All Xero invoices/quotes with admin delete for duplicates */}
              <XeroInvoicesCard customerId={id} isAdmin={!!user?.is_admin} lastReconciledAt={customer?.payments_reconciled_at} />

              {/* Notes */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-semibold font-['Manrope']">
                    Notes
                  </CardTitle>
                  <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-2.5 py-1 rounded-full" data-testid="notes-private-label">
                    <EyeOff className="w-3 h-3" />
                    Internal only - not visible to customer
                  </span>
                </CardHeader>
                <CardContent>
                  {customer.notes ? (
                    <p className="text-slate-700 whitespace-pre-wrap">{customer.notes}</p>
                  ) : (
                    <p className="text-slate-400 italic">No notes added</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="space-y-4">
              <div ref={contractPacksSectionRef}>
                <ContractPacksSection customerId={id} onChanged={fetchCustomer} />
              </div>
              <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold font-['Manrope']">
                    Documents & Pictures
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Organize with folders and control visibility for customer portal
                  </p>
                  {customer.documents?.length > 0 && (() => {
                    const synced = customer.documents.filter(d => d.sharepoint_item_id).length;
                    const need = customer.documents.length - synced;
                    return (
                      <div className="inline-flex items-center gap-2 mt-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs" data-testid="doc-backup-health">
                        <span className="font-medium text-slate-500">Backup health</span>
                        <span className="inline-flex items-center gap-1 text-emerald-600" data-testid="doc-backup-synced">
                          <Cloud className="w-3.5 h-3.5" />{synced} synced
                        </span>
                        {need > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-600" data-testid="doc-backup-need">
                            <CloudUpload className="w-3.5 h-3.5" />{need} need backup
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex gap-2">
                  {showNewFolderInput ? (
                    <div className="flex gap-2">
                      <Input
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="Folder name..."
                        className="w-40"
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                        data-testid="new-folder-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCreateFolder}
                        data-testid="create-folder-btn"
                      >
                        Create
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setShowNewFolderInput(true)}
                      data-testid="show-new-folder-btn"
                    >
                      <FolderPlus className="w-4 h-4 mr-2" />
                      New Folder
                    </Button>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx"
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="upload-btn"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload
                  </Button>
                  <Button
                    onClick={handleGenerateRams}
                    disabled={generatingRams}
                    variant="outline"
                    className="border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
                    data-testid="generate-rams-btn"
                    title="Generate (or regenerate) the RAMS document from this customer's data"
                  >
                    {generatingRams ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    {customer.documents?.some(d => d.doc_tag === 'rams') ? 'Regenerate RAMS' : 'Generate RAMS'}
                  </Button>
                  {customer.documents?.some(d => !d.sharepoint_item_id) && (
                    <Button
                      variant="outline"
                      onClick={async () => {
                        setBackingUp(prev => ({ ...prev, __all__: true }));
                        try {
                          const res = await api.post(`/customers/${id}/documents/sharepoint-backup-all`);
                          const r = res.data || {};
                          const ok = (r.backed_up || []).length;
                          const fail = (r.failed || []).length;
                          const miss = (r.missing_local_file || []).length;
                          const skip = (r.skipped_already_synced || []).length;
                          let msg = `Backed up ${ok} document${ok === 1 ? '' : 's'} to SharePoint`;
                          if (skip) msg += ` · ${skip} already synced`;
                          if (fail) msg += ` · ${fail} failed`;
                          if (miss) msg += ` · ${miss} missing locally`;
                          if (ok > 0) toast.success(msg); else toast.info(msg);
                          fetchCustomer();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Bulk SharePoint backup failed');
                        } finally {
                          setBackingUp(prev => { const n = { ...prev }; delete n.__all__; return n; });
                        }
                      }}
                      disabled={!!backingUp.__all__}
                      className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      data-testid="backup-all-sp-btn"
                      title="Upload any document not yet in SharePoint"
                    >
                      {backingUp.__all__ ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CloudUpload className="w-4 h-4 mr-2" />
                      )}
                      Backup all to SharePoint
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {customer.documents?.length > 0 ? (
                  <div className="space-y-6">
                    {(() => {
                      const docs = customer.documents || [];
                      const synced = docs.filter(d => d.sharepoint_item_id).length;
                      const need = docs.length - synced;
                      const times = docs.map(d => d.sharepoint_synced_at).filter(Boolean).sort();
                      const last = times.length ? times[times.length - 1] : null;
                      let rel = '';
                      if (last) {
                        const diff = Math.max(0, Date.now() - new Date(last).getTime());
                        const mins = Math.floor(diff / 60000);
                        rel = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
                      }
                      return (
                        <div
                          className={`rounded-lg border px-3 py-2 flex items-center gap-2 text-xs ${need > 0 ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-sky-200 bg-sky-50 text-sky-900'}`}
                          data-testid="customer-sharepoint-sync-banner"
                        >
                          <Cloud className="w-4 h-4 flex-shrink-0" />
                          <span className="flex-1">
                            <span className="font-semibold">SharePoint backup:</span>{' '}
                            <strong>{synced}</strong> of <strong>{docs.length}</strong> backed up
                            {need > 0 && <span className="text-amber-700"> · {need} need backup</span>}
                            {last && <span className="opacity-70"> · last synced {rel}</span>}
                            {!last && synced === 0 && <span className="opacity-70"> · not yet backed up</span>}
                          </span>
                        </div>
                      );
                    })()}
                    {/* Two-way SharePoint folder sync indicator */}
                    <div
                      className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 flex items-center gap-2 text-xs"
                      data-testid="customer-sharepoint-folder-sync-banner"
                    >
                      <RefreshCw className={`w-4 h-4 flex-shrink-0 ${syncingFolder ? 'animate-spin' : ''}`} />
                      <span className="flex-1">
                        <span className="font-semibold">SharePoint two-way sync:</span>{' '}
                        {customer.sp_folder_synced_at ? (
                          <>last synced {relativeTime(customer.sp_folder_synced_at)} · all folders kept in step with SharePoint</>
                        ) : (
                          <>not yet synced — runs automatically every 15 min once SharePoint is connected</>
                        )}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                        onClick={handleFolderSync}
                        disabled={syncingFolder}
                        data-testid="folder-sync-now-btn"
                      >
                        {syncingFolder ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        Sync now
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant={selectedFolder === '' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedFolder('')}
                        className={selectedFolder === '' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                        data-testid="folder-root"
                      >
                        <Folder className="w-4 h-4 mr-1" />
                        All Files ({customer.documents.length})
                      </Button>
                      <Button
                        variant={selectedFolder === '__root__' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedFolder('__root__')}
                        className={selectedFolder === '__root__' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                        data-testid="folder-unfiled"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Unfiled ({getDocumentsInFolder('').length})
                      </Button>
                      {folders.map((folder) => (
                        <Button
                          key={folder}
                          variant={selectedFolder === folder ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedFolder(folder)}
                          className={selectedFolder === folder ? 'bg-amber-500 hover:bg-amber-600' : ''}
                          data-testid={`folder-${folder}`}
                        >
                          <Folder className="w-4 h-4 mr-1" />
                          {folder} ({getDocumentsInFolder(folder).length})
                        </Button>
                      ))}
                    </div>

                    {/* Documents grid */}
                    <input
                      ref={repairInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleRepairFileSelected}
                      data-testid="repair-file-input"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {(selectedFolder === '' 
                        ? customer.documents 
                        : selectedFolder === '__root__'
                          ? getDocumentsInFolder('')
                          : getDocumentsInFolder(selectedFolder)
                      ).map((doc) => (
                        <div
                          key={doc.id}
                          className={`relative border rounded-lg overflow-hidden transition-all ${
                            doc.visible_to_customer 
                              ? 'border-emerald-300 bg-emerald-50/30' 
                              : 'border-slate-200 hover:border-amber-300'
                          }`}
                          data-testid={`document-${doc.id}`}
                        >
                          {/* Folder badge */}
                          {doc.folder && (
                            <div className="absolute top-2 left-2 z-10">
                              <Badge className="bg-slate-700 text-white text-xs">
                                <Folder className="w-3 h-3 mr-1" />
                                {doc.folder}
                              </Badge>
                            </div>
                          )}

                          {/* Invoice payment-status pill (driven by finance_status) */}
                          {doc.doc_tag === 'invoice' && (
                            <div className="absolute top-2 right-2 z-10" data-testid={`invoice-status-${doc.id}`}>
                              {doc.finance_status === 'PAID' ? (
                                <Badge className="bg-emerald-600 text-white text-xs">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                                </Badge>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleSendPaymentReminder(doc)}
                                  disabled={remindingInvoice === doc.id || !doc.source_invoice_id}
                                  title={doc.last_reminder_at
                                    ? `Reminder sent ✓ ${new Date(doc.last_reminder_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} — click to remind again`
                                    : 'Click to email the customer a payment reminder'}
                                  data-testid={`send-reminder-${doc.id}`}
                                  className="inline-flex items-center rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-2 py-0.5 transition-colors disabled:opacity-60"
                                >
                                  {remindingInvoice === doc.id ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Clock className="w-3 h-3 mr-1" />
                                  )}
                                  {doc.last_reminder_at ? 'Remind again' : 'Outstanding'}
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Preview area */}
                          <div 
                            className="h-32 bg-slate-100 flex items-center justify-center cursor-pointer relative group"
                            onClick={() => isPreviewable(doc.file_type) && handlePreviewDocument(doc)}
                          >
                            {doc.file_type?.startsWith('image/') ? (
                              <img 
                                src={`${API_URL}/api/files/${id}/${doc.id}`}
                                alt={doc.filename}
                                className="h-full w-full object-cover"
                              />
                            ) : doc.file_type?.startsWith('video/') ? (
                              <div className="flex flex-col items-center justify-center text-purple-400">
                                <Film className="w-8 h-8" />
                                <span className="text-xs mt-2 uppercase">video</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center text-slate-400">
                                {getFileIcon(doc.file_type)}
                                <span className="text-xs mt-2 uppercase">{doc.file_type?.split('/')[1] || 'file'}</span>
                              </div>
                            )}
                            {isPreviewable(doc.file_type) && (
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Eye className="w-8 h-8 text-white" />
                              </div>
                            )}
                          </div>

                          {/* Info area */}
                          <div className="p-3">
                            <p className="font-medium text-slate-900 truncate text-sm" title={doc.filename}>
                              {doc.filename}
                            </p>
                            <p className="text-xs text-slate-500 mb-3">
                              {formatDate(doc.uploaded_at)} • {doc.uploaded_by}
                            </p>

                            {['variation_of_contract', 'letter_of_authority'].includes(doc.doc_tag) && variationSignStatus[doc.id] && (() => {
                              const st = variationSignStatus[doc.id].status;
                              const map = {
                                signed: { cls: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle className="w-3 h-3" />, label: 'Signed' },
                                awaiting: { cls: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3 h-3" />, label: 'Awaiting signature' },
                                expired: { cls: 'bg-red-100 text-red-700', icon: <AlertCircle className="w-3 h-3" />, label: 'Sign link expired' },
                                not_sent: { cls: 'bg-slate-100 text-slate-600', icon: <FileText className="w-3 h-3" />, label: 'Not sent for signature' },
                              };
                              const cfg = map[st] || map.not_sent;
                              return (
                                <div
                                  className={`inline-flex items-center gap-1 mb-3 rounded-md text-[11px] font-medium px-2 py-0.5 ${cfg.cls}`}
                                  title={st === 'signed' && variationSignStatus[doc.id].signed_by ? `Signed by ${variationSignStatus[doc.id].signed_by}` : cfg.label}
                                  data-testid={`variation-sign-status-${doc.id}`}
                                >
                                  {cfg.icon} {cfg.label}
                                </div>
                              );
                            })()}

                            {doc.trustpilot_requested_at && (
                              <div
                                className="inline-flex items-center gap-1 mb-3 rounded-md bg-[#00b67a]/10 text-[#00b67a] text-[11px] font-medium px-2 py-0.5"
                                title={`Trustpilot review request emailed ${new Date(doc.trustpilot_requested_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                                data-testid={`trustpilot-requested-${doc.id}`}
                              >
                                <Star className="w-3 h-3 fill-current" /> Trustpilot review requested ✓
                              </div>
                            )}
                            
                            {/* Visibility toggle */}
                            <div className="flex items-center justify-between mb-3 p-2 bg-slate-50 rounded">
                              <div className="flex items-center gap-2">
                                {doc.visible_to_customer ? (
                                  <Eye className="w-4 h-4 text-emerald-600" />
                                ) : (
                                  <EyeOff className="w-4 h-4 text-slate-400" />
                                )}
                                <span className="text-xs text-slate-600">
                                  {doc.visible_to_customer ? 'Visible to customer' : 'Hidden from portal'}
                                </span>
                              </div>
                              <Switch
                                checked={doc.visible_to_customer || false}
                                onCheckedChange={() => handleToggleVisibility(doc.id, doc.visible_to_customer)}
                                data-testid={`visibility-${doc.id}`}
                              />
                            </div>

                            {/* Document tag */}
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                              {doc.doc_tag === 'quote' ? (
                                <Button variant="outline" size="sm" className="h-7 text-xs border-amber-300 bg-amber-50 text-amber-700" onClick={() => handleTagDocument(doc.id, '')} data-testid={`untag-${doc.id}`}>
                                  <FileText className="w-3 h-3 mr-1" />
                                  Quote ✓
                                </Button>
                              ) : (
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={() => handleTagDocument(doc.id, 'quote')} data-testid={`tag-quote-${doc.id}`}>
                                  <FileText className="w-3 h-3 mr-1" />
                                  Set as Quote
                                </Button>
                              )}
                              {doc.file_type?.startsWith('image/') && (
                                doc.doc_tag === 'cutout_image' ? (
                                  <Button variant="outline" size="sm" className="h-7 text-xs border-rose-300 bg-rose-50 text-rose-700" onClick={() => handleTagDocument(doc.id, '')} data-testid={`untag-cutout-${doc.id}`}>
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Cut-out image ✓
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={() => handleTagDocument(doc.id, 'cutout_image')} data-testid={`tag-cutout-${doc.id}`}>
                                    <FileText className="w-3 h-3 mr-1" />
                                    Set as Cut-out
                                  </Button>
                                )
                              )}
                              {user?.is_admin && doc.file_type?.startsWith('image/') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                  onClick={() => setDeleteImageTarget(doc)}
                                  data-testid={`delete-image-${doc.id}`}
                                  title="Delete this image from the CRM (admins only)"
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete
                                </Button>
                              )}
                              {doc.doc_tag === 'variation_of_contract' && (
                                doc.signed_at ? (
                                  <span className="inline-flex items-center gap-1 h-7 px-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded" data-testid={`variation-signed-${doc.id}`}>
                                    <CheckCircle className="w-3 h-3" /> Signed {new Date(doc.signed_at).toLocaleDateString()}
                                  </span>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                    onClick={() => handleSendSignLink(doc.id)}
                                    data-testid={`send-sign-link-${doc.id}`}
                                    title="Generate a tokenised public sign link and (optionally) email it to the customer"
                                  >
                                    <Send className="w-3 h-3 mr-1" />Send Sign Link
                                  </Button>
                                )
                              )}
                            </div>

                            {/* SharePoint backup status */}
                            <div className="flex items-center gap-2 mb-3">
                              {doc.sharepoint_item_id ? (
                                <a
                                  href={doc.sharepoint_web_url || '#'}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => { if (!doc.sharepoint_web_url) e.preventDefault(); }}
                                  className="inline-flex items-center gap-1.5 h-7 px-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 transition-colors"
                                  title="This document is backed up in SharePoint"
                                  data-testid={`sp-synced-${doc.id}`}
                                >
                                  <Cloud className="w-3 h-3" />
                                  Synced to SharePoint
                                </a>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                  onClick={() => handleBackupToSharePoint(doc.id)}
                                  disabled={!!backingUp[doc.id]}
                                  data-testid={`backup-sp-${doc.id}`}
                                  title="Upload this local file to SharePoint to protect it from container restarts"
                                >
                                  <CloudUpload className="w-3 h-3 mr-1" />
                                  {backingUp[doc.id] ? 'Backing up…' : 'Backup to SharePoint'}
                                </Button>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 flex-wrap">
                              {/* Re-extract — solar_proposal docs only.
                                  Re-runs Gemini on the stored PDF, refreshes
                                  mcs_solar_pv_data and replaces the linked
                                  MCS Schedule 1 standalone document. */}
                              {doc.type === 'solar_proposal' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReExtractProposal(doc.id)}
                                  disabled={reExtractingDocId === doc.id}
                                  className="h-8 text-xs border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                  data-testid={`re-extract-${doc.id}`}
                                  title="Re-run Gemini on this proposal to refresh the customer's MCS data"
                                >
                                  {reExtractingDocId === doc.id ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                  )}
                                  Re-extract
                                </Button>
                              )}
                              {doc.type === 'solar_proposal' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleExtractSystemDesign(doc.id)}
                                  disabled={extractingDesignId === doc.id}
                                  className="h-8 text-xs border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                  data-testid={`extract-design-${doc.id}`}
                                  title="Isolate the system design diagram (page 9) and share it to the customer portal"
                                >
                                  {extractingDesignId === doc.id ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Sun className="w-3 h-3 mr-1" />
                                  )}
                                  Extract design
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setMoveDocDialog({ open: true, doc })}
                                data-testid={`move-${doc.id}`}
                              >
                                <Folder className="w-3 h-3 mr-1" />
                                Move
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownload(doc)}
                                data-testid={`download-${doc.id}`}
                              >
                                <Download className="w-3 h-3 mr-1" />
                                Download
                              </Button>
                              {!doc.sharepoint_item_id && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                                  onClick={() => triggerRepair(doc.id)}
                                  disabled={repairing === doc.id}
                                  data-testid={`reupload-${doc.id}`}
                                  title="This file has no durable backup. Re-upload the original to repair it."
                                >
                                  {repairing === doc.id ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <CloudUpload className="w-3 h-3 mr-1" />
                                  )}
                                  {repairing === doc.id ? 'Uploading…' : 'Re-upload'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <FileText className="w-12 h-12 mb-4" />
                    <p>No documents uploaded</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload your first document
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Internal Notes Tab */}
          <TabsContent value="notes">
            <InternalNotes 
              customerId={id} 
              customerName={`${customer.first_name} ${customer.last_name}`} 
            />
          </TabsContent>

          {/* Installation Checklist Tab */}
          <TabsContent value="checklist">
            <InstallationChecklist
              customerId={id}
              customerName={`${customer.first_name} ${customer.last_name}`}
            />
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="history">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold font-['Manrope']">
                  Activity Log
                </CardTitle>
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="activity-filter-bar">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'correspondence', label: 'Correspondence', icon: <Mail className="w-3.5 h-3.5" /> },
                    { key: 'email_sent', label: 'Sent Emails', icon: <Mail className="w-3.5 h-3.5" /> },
                    { key: 'status_change', label: 'Status', icon: <ChevronRight className="w-3.5 h-3.5" /> },
                    { key: 'document', label: 'Documents', icon: <FileText className="w-3.5 h-3.5" /> },
                    { key: 'contract_pack', label: 'Contracts', icon: <FileSignature className="w-3.5 h-3.5" /> },
                    { key: 'other', label: 'Other' },
                  ].map(f => (
                    <Button
                      key={f.key}
                      variant={activityFilter === f.key ? 'default' : 'outline'}
                      size="sm"
                      className={activityFilter === f.key ? 'bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs' : 'h-7 text-xs'}
                      onClick={() => setActivityFilter(f.key)}
                      data-testid={`activity-filter-${f.key}`}
                    >
                      {f.icon && <span className="mr-1">{f.icon}</span>}
                      {f.label}
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const statusEntries = (customer.status_history || []).map(e => ({
                    type: 'status_change',
                    description: `Status changed to ${STATUS_DISPLAY_NAMES[e.status] || e.status}`,
                    user_name: e.changed_by,
                    timestamp: e.changed_at,
                    notes: e.notes,
                    status: e.status,
                  }));
                  const activityEntries = (customer.activity_history || []).filter(
                    e => e.type !== 'status_change'
                  );
                  // Map MS365 email correspondence into timeline entries
                  const correspondenceEntries = customerEmails.map(e => ({
                    type: 'correspondence',
                    direction: e.direction,
                    description: e.direction === 'inbound'
                      ? `Email from ${e.from_name || e.from_address}`
                      : `Email to ${e.customer_email || (e.to_addresses && e.to_addresses[0]) || 'recipient'} via ${e.mailbox || e.from_address || 'CRM'}`,
                    subject: e.subject,
                    body_preview: e.body_preview,
                    mailbox: e.mailbox,
                    from_address: e.from_address,
                    ms365_id: e.ms365_id,
                    timestamp: e.received_at,
                    user_name: e.direction === 'inbound' ? (e.from_name || e.customer_name) : ((e.mailbox || e.from_address || 'CRM').split('@')[0]),
                  }));
                  let allEntries = [...statusEntries, ...activityEntries, ...correspondenceEntries].sort(
                    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
                  );
                  // Apply filter
                  if (activityFilter === 'correspondence') {
                    allEntries = allEntries.filter(e => e.type === 'correspondence');
                  } else if (activityFilter === 'email_sent') {
                    allEntries = allEntries.filter(e => e.type === 'email_sent' || e.type === 'email_opened');
                  } else if (activityFilter === 'status_change') {
                    allEntries = allEntries.filter(e => e.type === 'status_change');
                  } else if (activityFilter === 'document') {
                    allEntries = allEntries.filter(e => e.type === 'document_upload' || e.type === 'document_delete');
                  } else if (activityFilter === 'contract_pack') {
                    allEntries = allEntries.filter(e => (e.type || '').startsWith('contract_pack_'));
                  } else if (activityFilter === 'other') {
                    allEntries = allEntries.filter(e => !['email_sent', 'email_opened', 'status_change', 'document_upload', 'document_delete'].includes(e.type) && !(e.type || '').startsWith('contract_pack_'));
                  }
                  if (allEntries.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                        <Clock className="w-12 h-12 mb-4" />
                        <p>{activityFilter !== 'all' ? `No ${activityFilter === 'email_sent' ? 'email' : activityFilter === 'document' ? 'document' : activityFilter === 'status_change' ? 'status' : ''} activity recorded` : 'No activity recorded'}</p>
                        {activityFilter !== 'all' && (
                          <Button variant="link" size="sm" className="mt-2 text-amber-600" onClick={() => setActivityFilter('all')}>
                            Show all activity
                          </Button>
                        )}
                      </div>
                    );
                  }
                  const getIcon = (type) => {
                    switch (type) {
                      case 'status_change': return <ChevronRight className="w-3.5 h-3.5 text-amber-500" />;
                      case 'document_upload': return <Upload className="w-3.5 h-3.5 text-blue-500" />;
                      case 'document_delete': return <Trash2 className="w-3.5 h-3.5 text-red-500" />;
                      case 'email_sent': return <Mail className="w-3.5 h-3.5 text-purple-500" />;
                      case 'email_opened': return <Eye className="w-3.5 h-3.5 text-green-500" />;
                      case 'correspondence': return <Mail className="w-3.5 h-3.5 text-sky-500" />;
                      case 'customer_update': return <Edit className="w-3.5 h-3.5 text-slate-500" />;
                      case 'customer_assignment': return <UserPlus className="w-3.5 h-3.5 text-indigo-500" />;
                      case 'note': return <MessageSquareText className="w-3.5 h-3.5 text-teal-500" />;
                      case 'whatsapp_message': return <MessageSquareText className="w-3.5 h-3.5 text-green-600" />;
                      case 'checklist_update': return <ClipboardCheck className="w-3.5 h-3.5 text-emerald-500" />;
                      case 'portal_login': return <ExternalLink className="w-3.5 h-3.5 text-cyan-500" />;
                      case 'call_log': return <PhoneCall className="w-3.5 h-3.5 text-green-600" />;
                      case 'contract_pack_sent': return <FileSignature className="w-3.5 h-3.5 text-emerald-600" />;
                      case 'contract_pack_viewed': return <Eye className="w-3.5 h-3.5 text-sky-600" />;
                      case 'contract_pack_emailed': return <Send className="w-3.5 h-3.5 text-sky-600" />;
                      case 'contract_pack_reminder_sent': return <Bell className="w-3.5 h-3.5 text-amber-600" />;
                      case 'dno_chaser_sent': return <Bell className="w-3.5 h-3.5 text-red-600" />;
                      case 'dno_detail_requested': return <Send className="w-3.5 h-3.5 text-amber-600" />;
                      case 'contract_pack_customer_signed': return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
                      case 'contract_pack_admin_signed':
                      case 'contract_pack_fully_signed': return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />;
                      default: return <Clock className="w-3.5 h-3.5 text-slate-400" />;
                    }
                  };
                  const getBorderColor = (type) => {
                    switch (type) {
                      case 'status_change': return 'border-amber-400';
                      case 'document_upload': return 'border-blue-400';
                      case 'document_delete': return 'border-red-400';
                      case 'email_sent': return 'border-purple-400';
                      case 'email_opened': return 'border-green-400';
                      case 'correspondence': return 'border-sky-400';
                      case 'customer_update': return 'border-slate-400';
                      case 'customer_assignment': return 'border-indigo-400';
                      case 'note': return 'border-teal-400';
                      case 'checklist_update': return 'border-emerald-400';
                      case 'portal_login': return 'border-cyan-400';
                      case 'call_log': return 'border-green-500';
                      case 'contract_pack_sent':
                      case 'contract_pack_customer_signed':
                      case 'contract_pack_admin_signed':
                      case 'contract_pack_fully_signed': return 'border-emerald-500';
                      case 'contract_pack_viewed':
                      case 'contract_pack_emailed': return 'border-sky-500';
                      case 'contract_pack_reminder_sent': return 'border-amber-500';
                      case 'dno_chaser_sent': return 'border-red-500';
                      case 'dno_detail_requested': return 'border-amber-500';
                      default: return 'border-slate-300';
                    }
                  };
                  const getEmailTypeBadge = (emailType) => {
                    switch (emailType) {
                      case 'manual': return <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">Manual Email</Badge>;
                      case 'status_update': return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Status Email</Badge>;
                      case 'status_notification': return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Auto Status Email</Badge>;
                      case 'portal_welcome': return <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">Portal Welcome</Badge>;
                      case 'portal_password_reset': return <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">Password Reset</Badge>;
                      case 'tracking': return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Email Opened</Badge>;
                      default: return null;
                    }
                  };
                  return (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                      <div className="space-y-4">
                        {allEntries.map((entry, index) => (
                          <div key={index} className="relative flex gap-4 pl-10" data-testid={`activity-entry-${index}`}>
                            <div className={`absolute left-2 w-5 h-5 rounded-full bg-white border-2 ${getBorderColor(entry.type)} flex items-center justify-center`}>
                              {getIcon(entry.type)}
                            </div>
                            <div className="flex-1 pb-2">
                              <div className="flex items-center gap-3 mb-0.5 flex-wrap">
                                {entry.type === 'status_change' && entry.status && (
                                  <Badge className={`${STATUS_COLORS[entry.status]} border text-xs`}>
                                    {STATUS_DISPLAY_NAMES[entry.status]}
                                  </Badge>
                                )}
                                {entry.type === 'email_sent' && entry.email_type && getEmailTypeBadge(entry.email_type)}
                                {entry.type === 'correspondence' && (
                                  <Badge className={entry.direction === 'inbound'
                                    ? 'bg-sky-100 text-sky-700 border-sky-200 text-xs'
                                    : 'bg-violet-100 text-violet-700 border-violet-200 text-xs'
                                  }>
                                    {entry.direction === 'inbound' ? 'Received' : 'Sent'}
                                  </Badge>
                                )}
                                {entry.type === 'call_log' && (
                                  <Badge className={
                                    entry.call_kind === 'received' ? 'bg-green-100 text-green-700 border-green-200 text-xs'
                                    : entry.call_kind === 'voicemail' ? 'bg-amber-100 text-amber-700 border-amber-200 text-xs'
                                    : entry.call_kind === 'outbound' ? 'bg-sky-100 text-sky-700 border-sky-200 text-xs'
                                    : 'bg-rose-100 text-rose-700 border-rose-200 text-xs'
                                  }>
                                    {entry.call_kind === 'received' ? 'Received' : entry.call_kind === 'voicemail' ? 'Voicemail' : entry.call_kind === 'outbound' ? 'Outbound' : 'Missed'}
                                  </Badge>
                                )}
                                <span className="text-sm text-slate-700">{entry.description}</span>
                                <span className="text-xs text-slate-400">
                                  {formatDateTime(entry.timestamp)}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500">
                                by <span className="font-medium">{entry.user_name || 'System'}</span>
                              </p>
                              {entry.notes && (
                                <p className="text-xs text-slate-500 mt-0.5 italic">"{entry.notes}"</p>
                              )}
                              {entry.type === 'correspondence' && entry.subject && (
                                <div className="mt-1 bg-slate-50 rounded px-2.5 py-1.5 border border-slate-100 group/email">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium text-slate-700 truncate">{entry.subject}</p>
                                      {entry.body_preview && (
                                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{entry.body_preview}</p>
                                      )}
                                    </div>
                                    {entry.ms365_id && entry.mailbox && (
                                      <button
                                        type="button"
                                        onClick={() => openEmailViewer('ms365', { ms365_id: entry.ms365_id, mailbox: entry.mailbox, subject: entry.subject })}
                                        className="flex-shrink-0 text-[11px] font-medium text-sky-700 hover:text-sky-900 underline-offset-2 hover:underline whitespace-nowrap"
                                        data-testid={`view-email-${entry.ms365_id}`}
                                      >
                                        View full email →
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                              {entry.type === 'whatsapp_message' && (
                                <div className="mt-1 bg-green-50 rounded px-2.5 py-1.5 border border-green-100">
                                  <div className="flex items-center gap-1.5 mb-0.5">
                                    <MessageSquareText className="w-3 h-3 text-green-600" />
                                    <span className="text-[11px] font-medium text-green-700">
                                      WhatsApp · {entry.direction === 'outbound' ? 'Sent' : 'Received'}
                                      {entry.direction === 'outbound' && entry.user_name && entry.user_name !== 'WhatsApp' ? ` by ${entry.user_name}` : ''}
                                    </span>
                                  </div>
                                  {entry.body_preview && (
                                    <p className="text-xs text-slate-600 line-clamp-3 whitespace-pre-wrap">{entry.body_preview}</p>
                                  )}
                                </div>
                              )}
                              {entry.type === 'email_sent' && entry.email_log_id && (
                                <div className="mt-1">
                                  <button
                                    type="button"
                                    onClick={() => openEmailViewer('email_log', { customer_id: id, email_log_id: entry.email_log_id, subject: entry.description })}
                                    className="text-[11px] font-medium text-purple-700 hover:text-purple-900 underline-offset-2 hover:underline"
                                    data-testid={`view-email-log-${entry.email_log_id}`}
                                  >
                                    View full email →
                                  </button>
                                </div>
                              )}
                              {entry.type === 'call_log' && entry.has_recording && entry.call_log_id && (
                                <div className="mt-1">
                                  <CallRecordingPlayer callLogId={entry.call_log_id} />
                                </div>
                              )}

                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Status Change Dialog with Email Preview */}
        <Dialog open={statusDialog.open} onOpenChange={(open) => { if (!open) setStatusDialog({ open: false, newStatus: '' }); }}>
          <DialogContent className="max-w-[85vw] w-[85vw] h-[80vh] flex flex-col p-0 gap-0" data-testid="status-email-dialog">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Update Status to{' '}
                  <Badge className={`${STATUS_COLORS[statusDialog.newStatus]} border`}>
                    {STATUS_DISPLAY_NAMES[statusDialog.newStatus]}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  {customer?.email ? 'Optionally send an email to the customer with the update.' : 'Customer has no email address on file.'}
                </DialogDescription>
              </DialogHeader>
            </div>

            {/* Notes row */}
            <div className="px-6 py-3 border-b border-slate-100 flex-shrink-0">
              <label className="text-sm font-medium text-slate-700">Status Change Notes (optional)</label>
              <Textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                className="mt-1 min-h-[40px] max-h-[60px]"
                data-testid="status-notes"
              />
            </div>

            {/* Email toggle */}
            {customer?.email && (
              <div className="px-6 py-2 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Switch checked={emailEnabled} onCheckedChange={setEmailEnabled} data-testid="email-toggle" />
                  <span className="text-sm font-medium text-slate-700">Send email to {customer.email}</span>
                  <span className="text-xs text-slate-400">(CC: hello@helioses.co.uk)</span>
                </div>
              </div>
            )}

            {/* Template selector */}
            {emailEnabled && customer?.email && (
              <div className="px-6 py-2 border-b border-slate-100 flex items-center gap-3 flex-shrink-0">
                <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Template:</label>
                <Select value={selectedTemplateId || 'default'} onValueChange={(v) => handleTemplateChange(v === 'default' ? null : v)}>
                  <SelectTrigger className="flex-1" data-testid="template-select-detail">
                    <SelectValue placeholder="Select template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default — {STATUS_DISPLAY_NAMES[statusDialog.newStatus]}</SelectItem>
                    {availableTemplates.filter(t => !t.is_default).map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.is_manual ? '[Custom] ' : ''}{t.label || t.subject || t.trigger}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Split-View Email Editor */}
            {emailEnabled && customer?.email && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {/* Subject */}
                <div className="px-6 py-2 border-b border-slate-100 flex-shrink-0 flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Subject:</label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="flex-1"
                    data-testid="email-subject"
                  />
                </div>
                <div className="flex-1 grid grid-cols-2 min-h-0 overflow-hidden" data-testid="email-split-view">
                  {/* Left: HTML Editor */}
                  <div className="flex flex-col border-r border-slate-200 min-h-0">
                    <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex-shrink-0">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Edit Email</span>
                    </div>
                    {/* Variable pills */}
                    <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex-shrink-0 flex flex-wrap gap-1 max-h-[60px] overflow-y-auto">
                      {['{{first_name}}','{{last_name}}','{{name}}','{{email}}','{{phone}}','{{address}}','{{city}}','{{postcode}}','{{system_size}}','{{system_total_cost}}','{{deposit_amount}}','{{setup_url}}','{{portal_url}}','{{reset_link}}','{{status}}'].map(v => (
                        <button key={v} type="button" className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-mono"
                          onClick={() => { setEmailBody(prev => prev + v); }}
                        >{v}</button>
                      ))}
                    </div>
                    <div className="flex-1 min-h-0 relative">
                      <textarea
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        className="absolute inset-0 w-full h-full resize-none font-mono text-sm p-4 bg-slate-900 text-emerald-300 border-0 focus:outline-none focus:ring-0"
                        style={{ tabSize: 2 }}
                        spellCheck={false}
                        data-testid="email-body-editor"
                      />
                    </div>
                  </div>
                  {/* Right: Live Preview */}
                  <div className="flex flex-col min-h-0">
                    <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex-shrink-0">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Preview</span>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-100 p-4">
                      <div className="max-w-xl mx-auto bg-white rounded-lg shadow-sm border overflow-hidden">
                        <div className="bg-slate-50 px-4 py-2 border-b space-y-1">
                          <div className="flex items-center gap-2 text-xs"><span className="font-medium text-slate-500 w-12">To:</span><span>{customer?.first_name} {customer?.last_name} &lt;{customer?.email}&gt;</span></div>
                          <div className="flex items-center gap-2 text-xs"><span className="font-medium text-slate-500 w-12">CC:</span><span>hello@helioses.co.uk</span></div>
                          <div className="flex items-center gap-2 text-xs"><span className="font-medium text-slate-500 w-12">Subject:</span><span className="font-medium text-slate-900">{emailSubject || '(No subject)'}</span></div>
                        </div>
                        <div className="p-5" data-testid="email-preview-body">
                          {emailBody ? (
                            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: emailBody }} />
                          ) : (
                            <p className="text-sm text-slate-400">No email content</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0 bg-white">
              <Button variant="outline" onClick={() => setStatusDialog({ open: false, newStatus: '' })} data-testid="cancel-status-change">
                Cancel
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleStatusChange(false)}
                  disabled={updating || sendingEmail}
                  data-testid="update-without-email"
                >
                  {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Update Without Email
                </Button>
                {customer?.email && emailEnabled && (
                  <Button
                    onClick={() => handleStatusChange(true)}
                    disabled={updating || sendingEmail}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="update-and-send-email"
                  >
                    {(updating || sendingEmail) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Update & Send Email
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Email Send Confirmation for Status Change */}
        <Dialog open={emailConfirmOpen} onOpenChange={setEmailConfirmOpen}>
          <DialogContent className="max-w-md" aria-describedby="confirm-status-email-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <Send className="w-5 h-5" />
                Confirm Send Email
              </DialogTitle>
            </DialogHeader>
            <p id="confirm-status-email-desc" className="text-sm text-slate-600 dark:text-slate-400">
              This will update the status and send an email to:
            </p>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-1.5 text-sm">
              <div><span className="font-medium text-slate-500">To:</span> {customer?.email}</div>
              <div><span className="font-medium text-slate-500">Subject:</span> {emailSubject}</div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => setEmailConfirmOpen(false)} data-testid="cancel-status-email">Go Back</Button>
              <Button onClick={() => executeStatusChange(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-status-email-btn">
                <Send className="w-4 h-4 mr-2" /> Confirm & Send
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Document Preview Dialog */}
        <AlertDialog open={!!deleteImageTarget} onOpenChange={(o) => !o && setDeleteImageTarget(null)}>
          <AlertDialogContent data-testid="delete-image-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this image?</AlertDialogTitle>
              <AlertDialogDescription>
                "{deleteImageTarget?.filename}" will be removed from the CRM. If this image is
                synced from SharePoint it may re-appear on the next sync — remove it in SharePoint
                too to delete it permanently. This cannot be undone in the CRM.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-image-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); handleDeleteImage(); }}
                disabled={deletingImage}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="delete-image-confirm"
              >
                {deletingImage ? 'Deleting…' : 'Delete image'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {previewDoc?.filename}
              </DialogTitle>
              <DialogDescription>
                Uploaded {previewDoc && formatDate(previewDoc.uploaded_at)} by {previewDoc?.uploaded_by}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {previewDoc?.file_type?.startsWith('image/') ? (
                <div className="flex justify-center">
                  <img 
                    src={`${API_URL}/api/files/${id}/${previewDoc?.id}`}
                    alt={previewDoc?.filename}
                    className="max-h-[60vh] object-contain rounded-lg"
                  />
                </div>
              ) : previewDoc?.file_type?.startsWith('video/') ? (
                <div className="flex justify-center">
                  <video
                    src={`${API_URL}/api/files/${id}/${previewDoc?.id}`}
                    controls
                    className="max-h-[60vh] w-full rounded-lg bg-black"
                  />
                </div>
              ) : previewDoc?.file_type === 'application/pdf' ? (
                <iframe
                  src={`${API_URL}/api/files/${id}/${previewDoc?.id}`}
                  title={previewDoc?.filename}
                  className="w-full h-[60vh] rounded-lg border"
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <FileText className="w-16 h-16 mb-4" />
                  <p>Preview not available for this file type</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDoc(null)}>
                Close
              </Button>
              <Button onClick={() => previewDoc && handleDownload(previewDoc)}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  window.open(`${API_URL}/api/files/${id}/${previewDoc?.id}`, '_blank');
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Move Document Dialog */}
        <Dialog open={moveDocDialog.open} onOpenChange={(open) => setMoveDocDialog({ open, doc: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Folder className="w-5 h-5" />
                Move Document
              </DialogTitle>
              <DialogDescription>
                Move "{moveDocDialog.doc?.filename}" to a folder
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-2">
              <Button
                variant={!moveDocDialog.doc?.folder ? 'default' : 'outline'}
                className={`w-full justify-start ${!moveDocDialog.doc?.folder ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                onClick={() => handleMoveDocument(moveDocDialog.doc?.id, '')}
                data-testid="move-to-root"
              >
                <FileText className="w-4 h-4 mr-2" />
                Root (No folder)
              </Button>
              {folders.map((folder) => (
                <Button
                  key={folder}
                  variant={moveDocDialog.doc?.folder === folder ? 'default' : 'outline'}
                  className={`w-full justify-start ${moveDocDialog.doc?.folder === folder ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                  onClick={() => handleMoveDocument(moveDocDialog.doc?.id, folder)}
                  data-testid={`move-to-${folder}`}
                >
                  <Folder className="w-4 h-4 mr-2" />
                  {folder}
                </Button>
              ))}
              {folders.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  No folders created yet. Create a folder first using the "New Folder" button.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveDocDialog({ open: false, doc: null })}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Post-Completion: Archive Customer Prompt */}
        <Dialog open={archivePrompt} onOpenChange={(open) => !open && setArchivePrompt(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-slate-600" />
                Archive this customer?
              </DialogTitle>
              <DialogDescription>
                <span className="font-medium">{customer?.first_name} {customer?.last_name}</span> is now marked as <span className="font-semibold text-emerald-700">Completed</span>. Would you like to move them to <span className="font-semibold">Archive/Lost</span> to remove them from the active pipeline?
              </DialogDescription>
            </DialogHeader>
            <div className="p-4 rounded-lg border bg-slate-50 border-slate-200">
              <p className="text-sm text-slate-700">
                Archived customers stay searchable from the Archive view but no longer clutter the main customers list or Kanban board.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setArchivePrompt(false)}
                data-testid="archive-prompt-skip"
                disabled={archiving}
              >
                Keep as Completed
              </Button>
              <Button
                onClick={handleArchiveNow}
                className="bg-slate-700 hover:bg-slate-800"
                data-testid="archive-prompt-confirm"
                disabled={archiving}
              >
                <Archive className="w-4 h-4 mr-2" />
                {archiving ? 'Archiving…' : 'Archive Customer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Archive/Complete Warning Dialog */}
        <Dialog open={archiveWarning.open} onOpenChange={(open) => !open && setArchiveWarning({ open: false, newStatus: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {archiveWarning.newStatus === 'archived' ? 'Archive Customer' : 'Complete Customer'}
              </DialogTitle>
              <DialogDescription>
                {archiveWarning.newStatus === 'archived'
                  ? <>This will move <span className="font-medium">{customer?.first_name} {customer?.last_name}</span> to <span className="font-semibold text-slate-700">Archive/Lost</span>. They will be removed from the active pipeline.</>
                  : <>This will mark <span className="font-medium">{customer?.first_name} {customer?.last_name}</span> as <span className="font-semibold text-emerald-700">Completed</span>. They will be removed from the active pipeline.</>
                }
              </DialogDescription>
            </DialogHeader>
            <div className={`p-4 rounded-lg border ${archiveWarning.newStatus === 'archived' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${archiveWarning.newStatus === 'archived' ? 'text-red-500' : 'text-amber-500'}`} />
                <p className={`text-sm ${archiveWarning.newStatus === 'archived' ? 'text-red-800' : 'text-amber-800'}`}>
                  This customer will no longer appear in the main customers list. They can be viewed in the Archive section under Customers.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setArchiveWarning({ open: false, newStatus: null })}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmArchive}
                className={archiveWarning.newStatus === 'archived' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                data-testid="confirm-archive-detail"
              >
                <Archive className="w-4 h-4 mr-2" />
                {archiveWarning.newStatus === 'archived' ? 'Move to Archive' : 'Mark Complete & Archive'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Email Dialog */}
        <SendEmailDialog
          open={emailDialogOpen}
          onOpenChange={(o) => {
            setEmailDialogOpen(o);
            if (!o) setEmailPresetTrigger(null);
          }}
          customer={customer}
          onEmailSent={() => { fetchCustomer(); setScheduledEmailRefresh(k => k + 1); }}
          presetTrigger={emailPresetTrigger}
        />

        {/* Message on WhatsApp Dialog */}
        <WhatsAppComposeDialog
          open={waComposeOpen}
          onOpenChange={setWaComposeOpen}
          phone={customer.whatsapp_number || customer.phone || ''}
          customerId={customer.id}
          customerName={`${customer.first_name || ''} ${customer.last_name || ''}`.trim()}
          defaultParams={[
            customer.first_name || '',
            customer.confirmed_install_date
              ? formatDate(customer.confirmed_install_date)
              : (customer.provisional_install_date ? formatDate(customer.provisional_install_date) : ''),
          ]}
          onSent={() => fetchCustomer()}
        />


        {/* Send Variation Sign Link Dialog */}
        <SendSignLinkDialog
          open={signLinkDialog.open}
          onOpenChange={(o) => !o && setSignLinkDialog({ open: false, doc: null })}
          customer={customer}
          document={signLinkDialog.doc}
          onSent={() => fetchCustomer()}
        />

        {/* Pop-out email viewer (timeline → View full email) */}
        <EmailViewDialog
          open={emailViewer.open}
          onOpenChange={(o) => !o && setEmailViewer({ open: false, source: null, params: null })}
          source={emailViewer.source}
          params={emailViewer.params}
        />

        {/* Xero Invoice Dialog */}
        <XeroInvoiceDialog
          open={xeroInvoiceOpen}
          onOpenChange={setXeroInvoiceOpen}
          customer={customer}
          onInvoiceCreated={fetchCustomer}
        />

        {/* Contract Pack Wizard */}
        <ContractPackWizard
          open={contractPackOpen}
          onClose={() => setContractPackOpen(false)}
          customerId={id}
          customerName={customer ? `${customer.first_name} ${customer.last_name}` : ''}
          jobLabel={customer?.job_label || ''}
          onSent={() => { setContractPackOpen(false); fetchCustomer(); }}
        />

        {/* DNO detail request — shows the shareable secure link after sending */}
        <Dialog open={!!dnoReqResult} onOpenChange={(o) => { if (!o) setDnoReqResult(null); }}>
          <DialogContent className="max-w-md" data-testid="dno-request-dialog">
            <DialogHeader>
              <DialogTitle>Request sent</DialogTitle>
              <DialogDescription>
                {dnoReqResult?.emailed
                  ? `We've emailed ${customer?.email || 'the customer'} a request for ${(dnoReqResult?.items || []).map(i => i === 'mpan' ? 'their MPAN' : 'a cut-out photo').join(' and ')}.`
                  : 'A secure request link has been created.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Share this secure link (no login needed) — or the customer can log in to their portal to upload:
              </p>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={dnoReqResult?.public_url || ''}
                  className="text-xs font-mono"
                  data-testid="dno-request-link"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  size="sm"
                  onClick={() => {
                    navigator.clipboard?.writeText(dnoReqResult?.public_url || '');
                    setDnoReqCopied(true);
                    toast.success('Link copied');
                    setTimeout(() => setDnoReqCopied(false), 2000);
                  }}
                  data-testid="dno-request-copy-btn"
                >
                  {dnoReqCopied ? <CheckCircle className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-400">Link expires in 14 days.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDnoReqResult(null)} data-testid="dno-request-close-btn">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Update Proposal Dialog — uploads a new proposal PDF, auto-applies
            extracted equipment, then routes to Variation / Replace / New. */}
        <UpdateProposalDialog
          open={updateProposalOpen}
          onOpenChange={setUpdateProposalOpen}
          customer={customer}
          onApplied={fetchCustomer}
          onOpenVariation={() => setVariationDialogOpen(true)}
          onOpenContractWizard={() => setContractPackOpen(true)}
        />

        {/* Variation of Contract — triggered by Update Proposal when a
            signed contract is on file. Auto-fills from the agreed snapshot. */}
        <VariationOfContractDialog
          open={variationDialogOpen}
          onOpenChange={setVariationDialogOpen}
          customer={customer}
          onSaved={fetchCustomer}
        />

        {/* Raise Variation router — smart dialog that inspects the contract
            state and routes to wizard / mark-signed-dochub / variation flow. */}
        <RaiseVariationDialog
          open={raiseVariationOpen}
          onOpenChange={setRaiseVariationOpen}
          customerId={id}
          currentTotal={customer?.system_total_cost}
          customerEmail={customer?.email}
          onSuccess={fetchCustomer}
          onOpenWizard={() => setContractPackOpen(true)}
          onOpenVariation={() => setVariationDialogOpen(true)}
        />

        {/* Letter of Authority generator */}
        <LoaGenerateDialog
          open={loaGenerateOpen}
          onOpenChange={setLoaGenerateOpen}
          customer={customer}
          onGenerated={fetchCustomer}
        />

        {/* Link Portal Account Dialog */}
        <Dialog open={portalLinkDialog} onOpenChange={setPortalLinkDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-600" />
                Link Portal Account
              </DialogTitle>
              <DialogDescription>
                Link an existing portal account or create a new one for {customer?.first_name} {customer?.last_name}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {!portalCreateMode ? (
                <>
                  {unlinkedPortalUsers.length > 0 ? (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Select an unlinked portal account</label>
                      <Select value={selectedPortalUserId} onValueChange={setSelectedPortalUserId}>
                        <SelectTrigger data-testid="select-portal-user">
                          <SelectValue placeholder="Choose a portal account..." />
                        </SelectTrigger>
                        <SelectContent>
                          {unlinkedPortalUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.first_name} {u.last_name} ({u.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No unlinked portal accounts available.</p>
                  )}
                  <div className="border-t pt-3">
                    <Button variant="outline" size="sm" className="w-full" onClick={() => setPortalCreateMode(true)} data-testid="create-portal-mode-btn">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create New Portal Account for This Customer
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-blue-800">Create a new portal account using:</p>
                  <div className="space-y-1 text-sm text-blue-700">
                    <p><strong>Name:</strong> {customer?.first_name} {customer?.last_name}</p>
                    <p><strong>Email:</strong> {customer?.email}</p>
                  </div>
                  <p className="text-xs text-blue-600">A password setup email will be sent to the customer.</p>
                  <Button variant="ghost" size="sm" onClick={() => setPortalCreateMode(false)} className="text-blue-700">
                    Back to link existing account
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPortalLinkDialog(false)}>Cancel</Button>
              {portalCreateMode ? (
                <Button onClick={handleCreatePortalAccount} disabled={portalSaving} data-testid="create-portal-confirm-btn">
                  {portalSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Create & Link
                </Button>
              ) : (
                <Button onClick={handleLinkPortalAccount} disabled={!selectedPortalUserId || portalSaving} data-testid="link-portal-confirm-btn">
                  {portalSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                  Link Account
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RaiseTicketDialog
          open={raiseTicketOpen}
          onOpenChange={setRaiseTicketOpen}
          preselectedCustomer={customer}
        />
      </div>

      {/* Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="max-w-md" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BellRing className="w-5 h-5 text-amber-600" />
              Set Reminder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Reminder Message</Label>
              <Input
                value={reminderMsg}
                onChange={(e) => setReminderMsg(e.target.value)}
                placeholder="e.g. Follow up with customer about deposit"
                data-testid="reminder-message"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date & Time</Label>
              <Input
                type="datetime-local"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                data-testid="reminder-date"
              />
            </div>
            <div className="space-y-3 pt-1">
              <Label className="text-sm font-medium">Also send</Label>
              <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reminderSendEmail}
                  onChange={(e) => setReminderSendEmail(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                  data-testid="reminder-email-check"
                />
                <div>
                  <p className="text-sm font-medium text-slate-900">Email Reminder</p>
                  <p className="text-xs text-slate-500">Send an email reminder to your inbox when due</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reminderSendCalendar}
                  onChange={(e) => setReminderSendCalendar(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                  data-testid="reminder-calendar-check"
                />
                <div>
                  <p className="text-sm font-medium text-slate-900">Calendar Invite</p>
                  <p className="text-xs text-slate-500">Create a calendar event in Outlook/Teams at the due time</p>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateReminder} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-reminder-btn">
                <BellRing className="w-4 h-4 mr-2" />
                Set Reminder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ScheduleInstallationDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        customer={customer}
        isAdmin={!!user?.is_admin}
        onScheduled={fetchCustomer}
        onBookedAndShouldEmail={() => {
          // After a successful booking, immediately open the email dialog with
          // the install-scheduled template pre-selected so admin can confirm
          // with the customer in one click.
          setEmailPresetTrigger('installation_scheduled');
          setEmailDialogOpen(true);
        }}
      />

      <PostCreateInvoiceDialog
        open={invoicePrompt.open}
        customer={customer}
        defaultInvoiceType={invoicePrompt.type}
        triggerLabel={invoicePrompt.label}
        onClose={() => {
          setInvoicePrompt({ open: false, type: 'deposit', label: null });
          fetchCustomer();
        }}
      />
      <AiImageNameDialog
        prompt={aiPrompt}
        onConfirm={(name) => resolveAiPrompt(name)}
        onSkip={() => resolveAiPrompt(null)}
      />
      <PortalPreviewPanel customerId={id} open={portalPreviewOpen} onOpenChange={setPortalPreviewOpen} />
    </Layout>
  );
};